/**
 * A run.
 *
 * Difficulty is carried by the floor number and nothing else: the first floor
 * is small, flat, and holds a handful of slow things, and every floor after it
 * adds a little. There is no menu between you and this, and no state that
 * survives death except the scoreboard.
 */

import * as THREE from 'three';
import type { NavmeshGraph } from 'levelspec';
import type { GeneratedLevel } from 'levelspec/generate';
import { fromThree, toThree } from '../three/adapter.ts';
import type { Collider, Player } from '../three/player.ts';
import { attachWeaponModel, buildExitMarker, buildPickup, buildSignal, disposeTree } from './world.ts';
import { BRIEF, carveZones, contractFor, Director, HOLD_RADIUS, shareOut } from './director.ts';
import {
  BLAST_BONUS, BLAST_RANGE, DELIBERATE_BONUS, DELIBERATE_GAP, INTRINSIC,
  OVERPRESSURE_BONUS, OVERPRESSURE_GAP, RHYTHM_CAP, RHYTHM_STEP,
  SPINUP_GAIN, SPINUP_TIME,
} from './traits.ts';
import type { Trait } from './traits.ts';
import { Telemetry } from './telemetry.ts';
import {
  BROOD_COUNT, BROOD_SPAWN, ELITES, eliteFor, PHASE_STEP, PHASE_TRIGGER,
  REVENANT_AT, REVENANT_BACK, SIPHON_RANGE, SIPHON_RATE, type Elite,
} from './elites.ts';
import type { Contract, Zone } from './director.ts';

import { rollRoster, typeById, waveShape, type EnemyType, type WaveShape } from './enemies.ts';
import { fade, headDepth, makeBody, shapeBody, type Body } from './bodies.ts';
import { Effects } from './effects.ts';
import { Flow } from './flow.ts';
import { sound } from './audio.ts';
import { accentFor } from './materials.ts';
import type { Tier } from './quality.ts';
import { applyEffects, baseModifiers, weaponMultiplier, type Card, type Modifiers } from './cards.ts';
import { dropFor, familyOf, STARTER, weaponById, type Weapon, type WeaponAction } from './weapons.ts';

export type Phase = 'playing' | 'cleared' | 'dead';

export interface RunState {
  phase: Phase;
  depth: number;
  score: number;
  kills: number;
  health: number;
  maxHealth: number;
  ammo: number;
  reserve: number;
  reloading: boolean;
  weapon: Weapon;
  /** Weapons carried, cycled with the swap control. */
  carried: string[];
  /**
   * Rounds left in each carried weapon.
   *
   * Swapping used to hand you a full magazine, which meant the fastest reload
   * in the game was pressing swap twice and reloading was optional. A gun you
   * put down half empty is half empty when you pick it up.
   */
  mags: Record<string, number>;
  /**
   * Spare rounds for each carried weapon.
   *
   * One pool shared by every gun meant picking up a pistol threw away four
   * hundred rifle rounds, and enough ammo crates on one weapon pushed the pool
   * past the threshold that makes the sidearm endless — so a rifle could
   * quietly become infinite. Ammo belongs to the gun it fits.
   */
  stock: Record<string, number>;
  cards: string[];
  hitFlash: number;
  damageFlash: number;
  /** Screen-space angle to whatever last hit you, radians. */
  damageBearing: number;
  pickupFlash: number;
  message: string;
  messageFor: number;
  enemiesAlive: number;
  /** True once the floor is clear and the way out has opened. */
  exitOpen: boolean;
  /** True while the compass is pointing at the last enemies rather than the exit. */
  huntMode: boolean;
  /** Enemies still to kill before it opens. */
  remaining: number;
  /** Current recoil climb in radians, for the view model and the reticle. */
  recoil: number;
  /**
   * The cone the next round will actually go into, in radians.
   *
   * Published for the reticle. It is the same product the fire path uses —
   * the weapon's spread, the modifiers on it, the steadiness bonus for
   * standing still, and the heat from the burst so far — so what the reticle
   * shows is not an impression of accuracy, it is the number the bullet is
   * about to be drawn from.
   */
  spread: number;
  /** What this floor is asking for, and the three words that say so. */
  contract: Contract;
  objective: string;
  /** Which zone is live, and how many there are. Both 1-based for display. */
  zone: number;
  zones: number;
  /** 0..1 on a hold floor, and -1 on any other. */
  hold: number;
  /**
   * What finally did it, in words.
   *
   * A death you cannot attribute is a death you cannot learn from, and "you
   * died on floor nine" attributes nothing. Set on every hit and read only at
   * the end, so it is always the last thing that touched you.
   */
  killedBy: string;
  /**
   * Damage you can still take back, sitting on top of your health.
   *
   * Drawn as the pale part of the bar. It is the difference between a game
   * that rewards pushing and one that rewards hiding, and it is the single
   * most important number on the HUD after the health itself.
   */
  grey: number;
  /** Metres to the exit, for the arrow. */
  exitDistance: number;
  /** Screen-space angle to the exit, radians, 0 = straight ahead. */
  exitBearing: number;
  elapsed: number;
}

interface Enemy extends Body {
  stride: number;
  pos: THREE.Vector3;
  health: number;
  node: number;
  cooldown: number;
  hitFlash: number;
  slow: number;
  bob: number;
  /** Set the moment it dies, so on-kill effects cannot re-enter it. */
  dead: boolean;
  /** Seconds without getting any closer, for the anti-softlock nudge. */
  stuckFor: number;
  /** Closest this one has ever got to the player. */
  bestDist: number;
  /** Which half-stride it was on last frame, so a footfall lands once. */
  stepPhase: number;
  /**
   * Seconds left of arriving, during which it stands still and is see-through.
   *
   * An enemy that is walking at you on the frame it appears gives you nothing
   * to react to — it was not there, and then it is already a threat. This is
   * the beat in between: it materialises where its light is, and only then
   * does it become something that can move or shoot.
   */
  warmup: number;
  /** The light on its arrival, while it has one. */
  glow: THREE.PointLight | null;
  /**
   * The one the floor is about, on a hunt.
   *
   * Tougher, slower, and carrying a marker you can see across the map. Marking
   * something is worth more than adding a boss type: the player already knows
   * what a Brute does, and a Brute they have been told to kill is a fight they
   * can plan rather than a new set of rules to learn mid-run.
   */
  marked: boolean;
  /** The rule laid over it, if it is the one that is not like the others. */
  elite: Elite | null;
  /** A revenant only comes back once. */
  returned: boolean;
  /** What it started with, for the rules that are about fractions of it. */
  peak: number;
  /**
   * Released by the director, and therefore counted by it.
   *
   * A turret an engineer builds mid-fight is not part of the floor's roster —
   * it was never in a wave, and a zone that waited for it to die would be
   * waiting on something the builder can replace. Only what the director
   * handed out is what the director is owed back.
   */
  staged: boolean;
}

/** A candidate the player is pointing at, with everything aiming needs. */
interface AimTarget {
  e: Enemy;
  /** Unit vector from the eye to the target's centre of mass. */
  dir: THREE.Vector3;
  /** Radians off the crosshair. */
  angle: number;
  dist: number;
}

/** Actions that have to be worked by hand between rounds. */
const CYCLED_BY_HAND = new Set(['bolt', 'pump', 'lever', 'breech']);

/**
 * What the floor wants, in one sentence, once.
 *
 * The three-word brief on the HUD is the thing you glance at; this is the
 * thing you read on arrival. Neither of them explains the mechanism, because
 * the mechanism is the map — the signal is a ring on the floor and the target
 * has a marker over it, and a player who has seen either does not need a
 * paragraph about it.
 */
const OPENING: Record<Contract, string> = {
  sweep: 'Push through and clear it',
  hunt: 'One of them is marked. Kill it and go',
  hold: 'Stand in the signal until it reads',
};

/** How long an enemy spends arriving before it is allowed to do anything. */
const WARMUP = 1;

/**
 * The most spare rounds anything may hold.
 *
 * This used to be a magic band as well as a cap: the sidearm was given 900 and
 * the code that spends ammo skipped anything at or above it, so the gun you
 * start with never ran out. The reasoning was that being stranded on floor one
 * with an empty gun is a dead end rather than a difficulty curve, which is
 * true — but the cost was that ammunition never entered your head. You could
 * hold the trigger down for an entire run.
 *
 * So the sidearm is finite now, and the dead end is closed at the other end:
 * `TOPUP` is put back into it on arriving at every floor, so you always land
 * with something to shoot even if you emptied it on the way down. Running dry
 * mid-fight is a thing that can happen; running dry for ever is not.
 */
/**
 * How high a step is, for everything that has to agree about it.
 *
 * The navmesh bake joins two samples when they are within this of each other,
 * the player's step-up climbs this, and an enemy ignores anything shorter than
 * this when it walks. Those three used to be 0.45, 0.45 and 0.25, written in
 * three files, and the third one was the reason enemies stopped at kerbs the
 * player had just walked up. `tools/steps.ts` walks the mesh with both bodies.
 */
export const STEP = 0.45;

export const ENDLESS = 900;

/** Rounds the sidearm is topped up to on arriving at a floor. */
const TOPUP = 72;

interface Projectile {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  damage: number;
  /** Where it was fired from, so a hit can point back at the shooter. */
  from: THREE.Vector3;
  /** Downward acceleration. A lobbed shell arcs; a bolt does not. */
  gravity?: number;
  /** Radius over which it hurts on impact rather than only on contact. */
  splash?: number;
  /** What fired it, so a death can be attributed to a name. */
  by: string;
}

/**
 * What to call an enemy in a sentence.
 *
 * The elite rule when it has one, because that is what the player was fighting
 * — "a Revenant Brute" is the thing they will remember, and "a Brute" is not
 * the same fight.
 */
function nameOf(e: Enemy): string {
  return e.elite ? `a ${ELITES[e.elite].name} ${e.type.name}` : `a ${e.type.name}`;
}

/**
 * Aiming, for two thumbs.
 *
 * A phone gives you a drag, not a mouse, so precise aim is not on the table
 * and building the game as if it were means it is only playable by people who
 * already play these. Three things instead: a wide cone that counts as "on
 * target", a slow rotation toward whatever is in that cone, and shots that
 * bend to the target once they are inside it. Together they mean pointing
 * roughly at something is the same as aiming at it — which is what a
 * first-time player expects to happen anyway.
 */
/**
 * Recoil is a pattern, not a dice roll.
 *
 * A random cone per shot is simulation-honest and completely illegible: you
 * cannot tell a miss you caused from one the game rolled, so there is nothing
 * to learn and no reason to improve. Every round here leaves on exactly the
 * line the reticle is on, and what limits sustained fire is a climb that is
 * the same every time — ramping over the first three rounds, plateauing, then
 * easing off. Spray control becomes a skill instead of a lottery.
 */
function recoilKick(shot: number): number {
  if (shot < 3) return 0.35 + shot * 0.28;
  if (shot < 10) return 1.0;
  return Math.max(0.45, 1.0 - (shot - 10) * 0.045);
}

/**
 * And which way it walks while it climbs.
 *
 * Straight up for the first few, then a lean that reverses about once a
 * magazine — the shape every recognisable spray pattern has. Multiplied by the
 * weapon's own signed `sway`, so an SMG snakes, a rifle barely leans, and a
 * bolt action does not move at all. Deterministic, like the climb: this is
 * something to learn, not something to survive.
 */
function swayKick(shot: number): number {
  if (shot < 2) return 0;
  return Math.sin((shot - 2) * 0.42) * Math.min(1, (shot - 1) / 4);
}

/**
 * How hard one round throws the reticle, before the weapon's own multiplier.
 *
 * Larger than it was, because recovery is now immediate: what the player sees
 * per shot is the whole kick, and what accumulates over a burst is only the
 * part that could not get home in time. The number that matters is the
 * plateau — an assault rifle holding the trigger settles about eight degrees
 * up rather than climbing without limit — and that comes out of this and
 * `RECOIL_RETURN` together.
 */
/**
 * How much of a hit you are allowed to earn back, and how long you have.
 *
 * The old rule was passive regeneration after six quiet seconds, which is
 * kind and which teaches exactly the wrong lesson: the optimal play is to
 * disengage, stand behind something until the number goes up, and only then
 * come back — in a game whose streaks, close-range cards and push-forward
 * weapons are all built around the opposite. It also makes the quiet parts of
 * a floor the parts you are rewarded for extending.
 *
 * So most of a hit becomes recoverable rather than lost, and the only way to
 * recover it is to kill something. Hiding still saves your life; it just no
 * longer refills you. Doom's push-forward combat is the same idea with the
 * arithmetic done differently — enemies as a resource rather than only a
 * threat — and it is the reason that game can afford to be relentless.
 */
const GREY_SHARE = 0.6;
/** Seconds after the last hit before what is owed starts draining away. */
const GREY_WINDOW = 5;
/** How fast it drains once that is up, in health per second. */
const GREY_DRAIN = 9;
/**
 * A floor under the passive trickle, so floor one is still survivable.
 *
 * Somebody who has never held a mouse must not be able to reach an
 * unrecoverable position in the first minute, and grey health cannot help
 * them if they cannot kill anything. This is deliberately low — it is a floor
 * to stand on, not a reason to wait.
 */
const REST_HEALTH = 40;
/**
 * What a hunt's marked target is worth, before the floor's own scaling.
 *
 * Chosen against the starter pistol rather than in the abstract: 183 damage a
 * second and sixteen rounds a magazine, so this is a shade over one magazine
 * on floor one and a little over three by floor thirteen.
 */
const MARKED_HEALTH = 500;

/**
 * Seconds with something alive and nothing dying before the floor concedes.
 *
 * Longer than any lull a fight actually has — the breath between two zones is
 * under three seconds and the walk to the next one is a handful — and short
 * enough that a player who has genuinely lost the last enemy is not standing
 * around for a minute. It was fifty, which was correct and too slow: the
 * survivors get cleared, the next wave arrives, one of *those* ends up
 * somewhere unreachable, and at fifty seconds a cycle a floor with a deep
 * roster can spend its whole clock on it.
 */
const BARREN = 30;

const RECOIL_SCALE = 1.15;
/**
 * How fast the reticle comes home, per second.
 *
 * Tuned against a rifle's hundred-millisecond cycle: about half of each kick
 * is returned before the next round, so a burst climbs to a plateau in five or
 * six rounds and a single shot is visibly back on target before the player
 * could pull again.
 */
const RECOIL_RETURN = 7;
const FIRE_CONE = 0.2; // ~11°: close enough to the crosshair to shoot at
const ASSIST_CONE = 0.42; // ~24°: close enough to be nudged toward
const ASSIST_RATE = 1.5; // radians per second of free help, at most

export class Run {
  readonly state: RunState = {
    phase: 'playing',
    depth: 1,
    score: 0,
    kills: 0,
    health: 100,
    maxHealth: 100,
    ammo: STARTER.magazine,
    reserve: STARTER.pickupAmmo,
    reloading: false,
    weapon: STARTER,
    carried: [STARTER.id],
    mags: { [STARTER.id]: STARTER.magazine },
    stock: { [STARTER.id]: STARTER.pickupAmmo },
    cards: [],
    hitFlash: 0,
    damageFlash: 0,
    damageBearing: 0,
    pickupFlash: 0,
    message: '',
    messageFor: 0,
    enemiesAlive: 0,
    exitOpen: false,
    huntMode: false,
    remaining: 0,
    recoil: 0,
    spread: 0,
    contract: 'sweep',
    objective: BRIEF.sweep,
    zone: 1,
    zones: 1,
    hold: -1,
    grey: 0,
    killedBy: '',
    exitDistance: 0,
    exitBearing: 0,
    elapsed: 0,
  };

  /**
   * What actually happened, for anybody who wants to know rather than guess.
   *
   * Public and always on. It costs a handful of additions a frame, and the
   * alternative — switching it on when a question comes up — means the numbers
   * only ever exist for the runs somebody already suspected.
   */
  readonly telemetry = new Telemetry();

  mods: Modifiers = baseModifiers();
  /**
   * How much of the aim assist to apply, 0..1. A thumb needs all of it; a
   * mouse mostly wants to be left alone, and anything that pulls hard against
   * the hand feels broken even when it is helping.
   */
  assistStrength = 1;
  triggerHeld = false;
  /** Set on the frame the trigger goes down, for semi-automatic weapons. */
  triggerPressed = false;

  /**
   * Everything the run draws — enemies, pickups, tracers. Public because the
   * scene is torn down between floors and this has to be put back; it holds
   * the only visible evidence that any of the combat is happening.
   */
  readonly root = new THREE.Group();

  private scene: THREE.Scene;
  private player: Player;
  private enemies: Enemy[] = [];
  private pool: Enemy[] = [];
  private projectiles: Projectile[] = [];
  private pickups: THREE.Group[] = [];
  /**
   * Collected pickups, waiting a frame to be freed.
   *
   * A weapon on the floor is a freshly parsed model, with its own geometry and
   * its own materials — `clearFloor` has always known that and disposed them,
   * but the ones you *walked over* were simply spliced out of the array and
   * left to the garbage collector, which does not free GPU buffers. Ten floors
   * of collected weapons is ten floors of vertex buffers still resident.
   *
   * They are freed a frame late and one at a time on purpose. The frame you
   * pick a gun up on is already paying for a weapon swap, a sound, a message
   * and a view model change; deleting a few dozen buffers on top of that is
   * the one frame in the game where it would be felt.
   */
  private discarded: THREE.Object3D[] = [];
  private fx: Effects;

  private gen: GeneratedLevel | null = null;
  private graph: NavmeshGraph | null = null;
  private colliders: Collider[] = [];
  /**
   * Colliders bucketed by ground cell.
   *
   * Enemies need the same wall collision the player has, and doing it by
   * scanning every solid for every enemy on every axis every frame is twenty
   * enemies times four hundred solids times two axes — enough to matter on a
   * phone. Bucketing makes it a handful of candidates each.
   */
  private cells = new Map<string, Collider[]>();
  private static readonly CELL = 4;
  /** Which way the player is, from anywhere on the floor. Rebuilt per floor. */
  private flow: Flow | null = null;

  /**
   * Everything this floor will ever contain, decided when you arrive.
   *
   * The obvious design is a budget that drains as things spawn, and the floor
   * being clear when the budget is empty and nothing is standing. It does not
   * work: a spawn can fail — every candidate post too close, too far, or in
   * view — and a budget with less left in it than the cheapest enemy costs
   * never reaches zero, so the floor can never end. Deciding the roster up
   * front makes "is this floor finished" a question about a list rather than
   * about whether a spawn happened to succeed.
   */
  private roster: EnemyType[] = [];
  /**
   * The floor's clock: which zone is live and when the next thing comes out.
   *
   * Null only before the first `enter`, and on a map whose navmesh could not
   * be measured — in which case the whole floor is one zone and the loop is
   * exactly what it always was.
   */
  private director: Director | null = null;
  /** What contract the previous floor had, so this one does not repeat it. */
  private lastContract: Contract | undefined;
  /** The hunt's target, while it is standing. */
  private quarry: Enemy | null = null;
  /** The rule this floor's stand-out enemy plays by, if it has one. */
  private elite: Elite | null = null;
  /** Elite rules already read out once this run. */
  private readonly metElites = new Set<Elite>();
  /** Seconds until the siphon's beam is redrawn. */
  private siphonBeam = 0;
  /** Seconds until the signal's next tick. */
  private holdTick = 0;
  /** Health owed back, and how long before it starts draining. */
  private grey = 0;
  private greyFor = 0;
  /** Seconds the trigger has been held without letting go, for `spinup`. */
  private heldFor = 0;
  /** A `chamber` weapon whose bolt was worked for it by a hit to the head. */
  private chambered = false;
  /** Rounds fired since the magazine went in, for `third_shell`. */
  private sinceLoad = 0;
  /** Kills on the current `battle_rhythm`. */
  private rhythm = 0;
  /** Seconds left of the speed a `canter` weapon lent you. */
  private canter = 0;
  /** Actions whose rule has already been read out once this run. */
  private readonly explained = new Set<WeaponAction>();
  /** Consecutive failed spawn attempts, before falling back to anywhere legal. */
  private spawnMisses = 0;
  private toKill = 0;
  private killedHere = 0;
  private exitGroup = buildExitMarker();
  /** The ring you have to stand in, on a hold floor. Null on the others. */
  private signalGroup: THREE.Group | null = null;
  /**
   * The chevron over the marked target.
   *
   * An octahedron, unlit and above the tone map, drawn on top of everything —
   * which is deliberate: the whole point of a hunt is that you can see where
   * the target is through the map, so the floor is a chase rather than a
   * search. Its own object rather than a child of the body, because the body
   * comes out of a pool and goes back into it.
   */
  private readonly quarryMark = (() => {
    const m = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.3, 0),
      new THREE.MeshBasicMaterial({
        color: 0xff3158, toneMapped: false, fog: false,
        transparent: true, opacity: 0.92, depthTest: false,
      }),
    );
    m.renderOrder = 12;
    m.visible = false;
    return m;
  })();
  /** Where this floor started, for putting the player back properly. */
  private spawnPoint = new THREE.Vector3();
  private rescueWatch = 0;
  private rescueWindow = 0;
  private spawnTimer = 0;
  private fireTimer = 0;
  /** Rounds fired without letting go, which is what the pattern indexes. */
  private burst = 0;
  private sinceShot = 99;
  /** Camera climb owed back to the player once they stop firing. */
  private recoilOwed = 0;
  /** The same, sideways: the drift is given back so the pattern nets to zero. */
  private swayOwed = 0;
  /** Rounds still owed on a burst that the trigger has already paid for. */
  private burstLeft = 0;
  /**
   * Seconds of held time owed to the last substantial hit.
   *
   * Spent by `update` before anything else sees the frame, so physics, aim and
   * animation all agree that less time passed. Capped at a few frames: a hitch
   * you can feel is impact and a hitch you can measure is a bug.
   */
  private freeze = 0;
  private reloadTimer = 0;
  private sinceDamage = 99;
  /** Seconds since anything died on this floor. */
  private sinceKill = 0;
  /**
   * Brief invulnerability after a hit. Without it, three things reaching you
   * at once do three times the damage in the same instant and there is nothing
   * you could have done differently — which reads as the game cheating rather
   * than as a mistake you made.
   */
  private iFrames = 0;
  private exitPos = new THREE.Vector3();
  private rngSeed = 1;

  /**
   * Which fidelity the hostiles are built at.
   *
   * Only the enemy shell cares: real thin-film iridescence is a physical
   * material and costs several times a standard one per pixel, which is
   * affordable for a dozen bodies on a desktop and not on a phone.
   */
  private tier: Tier = 'balanced';

  constructor(scene: THREE.Scene, player: Player, tier: Tier = 'balanced') {
    this.scene = scene;
    this.player = player;
    this.tier = tier;
    this.fx = new Effects(tier);

    this.dimExit();
    this.root.add(this.fx.group, this.exitGroup, this.quarryMark);
    scene.add(this.root);
  }

  private rand(): number {
    this.rngSeed = (this.rngSeed * 1664525 + 1013904223) >>> 0;
    return this.rngSeed / 0x100000000;
  }

  // -------------------------------------------------------------------------

  /** Put the player into a freshly generated floor. */
  enter(gen: GeneratedLevel, colliders: Collider[]): void {
    this.clearFloor();
    /*
     * Arrive with something to shoot.
     *
     * The sidearm is finite now, so a floor cleared on the last few rounds
     * would otherwise be followed by a floor entered with none — and unlike a
     * dry rifle, a dry sidearm has nothing behind it. Topping up rather than
     * refilling: what you brought down is kept if it is more than this, so
     * husbanding it still means something.
     */
    const s = this.state;
    s.stock[STARTER.id] = Math.max(s.stock[STARTER.id] ?? 0, TOPUP);
    if (s.weapon.id === STARTER.id) s.reserve = Math.max(s.reserve, TOPUP);
    this.gen = gen;
    // The muzzle halo belongs to the floor it is fired on.
    this.fx.setAccent(accentFor(gen.spec.id));
    this.colliders = colliders;
    this.cells.clear();
    for (const c of colliders) {
      const x0 = Math.floor(c.box.min.x / Run.CELL);
      const x1 = Math.floor(c.box.max.x / Run.CELL);
      const z0 = Math.floor(c.box.min.z / Run.CELL);
      const z1 = Math.floor(c.box.max.z / Run.CELL);
      for (let x = x0; x <= x1; x++)
        for (let z = z0; z <= z1; z++) {
          const k = `${x},${z}`;
          let arr = this.cells.get(k);
          if (!arr) this.cells.set(k, (arr = []));
          arr.push(c);
        }
    }
    this.rngSeed = (gen.depth * 7919 + gen.spec.id.length * 104729) >>> 0 || 1;

    // The generator already baked this level and kept the graph — re-baking
    // here would double the time between floors for an identical answer.
    this.graph = gen.navmesh.graph ?? null;
    if (this.graph) {
      this.flow = new Flow(this.graph);
    }

    const spawn = toThree(gen.spawn[0], gen.spawn[1], gen.spawn[2] + 0.05);
    this.spawnPoint.copy(spawn);
    this.rescueWatch = this.player.rescues;
    this.rescues = 0;
    this.rescueWindow = 0;
    this.player.mode = 'walk';
    this.player.teleport(spawn);
    if (gen.spawnFacing !== undefined) {
      // Canonical heading is measured from +x in the XY plane; the player's yaw
      // is measured about +Y with 0 looking down -z. The adapter's basis makes
      // the conversion a quarter turn.
      this.player.yaw = -gen.spawnFacing - Math.PI / 2;
      this.player.pitch = 0;
    }
    // The way out does not exist yet. Hunting the last enemy on a map you have
    // already crossed is far better than racing to a door you can see from the
    // start, and it means the whole map gets used.
    this.exitPos.copy(spawn);
    this.state.exitOpen = false;
    this.dimExit();

    for (const p of gen.pickups) {
      // A weapon on the floor is decided here rather than on pickup, so the
      // gun you can see lying there is the gun you get.
      const variant = p.kind === 'weapon' ? (p.variant ?? dropFor(gen.depth, this.rand()).id) : p.variant;
      this.dropPickup(p.kind, variant, p.at);
    }

    const shape = waveShape(gen.depth);
    /*
     * Cut the map up, and decide what the floor is asking, before rolling what
     * is on it — because a hunt does not want the same amount of enemy.
     *
     * Measured over two hundred and twenty-two hunt floors: the same length as
     * a sweep, the same number of kills, and exactly twice the health gone —
     * 71 against 35. That is not the marked target being tough; replacing its
     * flat multiplier with a budget moved the contract's clear rate not at
     * all. It is where the roster ends up standing. A hunt puts two thirds of
     * it in the target's zone rather than a sweep's peak of under half, and
     * then deliberately leaves the stragglers alive, so by the time you reach
     * the mark the floor is not a zone's worth of enemy — it is all of it, at
     * once, behind you and in front of you.
     *
     * So a hunt gets a smaller floor. The contract keeps its character: still
     * concentrated, still nothing conveniently dying behind you, still a walk
     * into the worst room on the map. There is simply less standing in it.
     */
    const zones = this.graph
      ? carveZones(this.graph, gen.spawn, gen.exit, gen.enemyPosts)
      : [];
    const contract: Contract = zones.length
      ? contractFor(gen.depth, zones.length, () => this.rand(), this.lastContract)
      : 'sweep';
    const budget = Math.round(shape.budget * (contract === 'hunt' ? 0.65 : 1));
    // Bound, not passed: `rand` is a method and reads `this.rngSeed`.
    const fight = rollRoster(gen.depth, budget, () => this.rand());
    this.roster = fight.roster;
    this.bind = fight.bind;
    this.toKill = this.roster.length;
    this.spawnMisses = 0;
    this.killedHere = 0;
    this.sinceKill = 0;
    this.barren = 0;
    this.spawnTimer = shape.grace;

    /*
     * And hand the roster out along it.
     *
     * The roster itself is untouched: it was rolled from the seed above and
     * every enemy in it will appear. All that happens here is that it stops
     * being a bag emptied over the whole floorplan and becomes two, three or
     * four fights you walk into in order.
     */
    if (zones.length) {
      this.lastContract = contract;
      this.director = new Director(zones, contract, shareOut(this.roster, zones.length, contract), gen.depth);
      this.state.contract = contract;
      this.state.objective = BRIEF[contract];
      this.state.zones = zones.length;
      this.state.zone = 1;
      this.state.hold = contract === 'hold' ? 0 : -1;
      if (contract === 'hold') this.placeSignal(this.director.signal);
    } else {
      this.director = null;
      this.state.contract = 'sweep';
      this.state.objective = BRIEF.sweep;
      this.state.zones = 1;
      this.state.zone = 1;
      this.state.hold = -1;
    }
    this.quarry = null;
    /*
     * One rule per floor, laid over whichever thing the floor was going to
     * mark anyway — or, on a floor with nothing marked, over the toughest
     * thing in the last zone. Two rules at once is a fight nobody can read.
     */
    this.elite = eliteFor(gen.depth, () => this.rand());
    this.grey = 0;
    this.greyFor = 0;
    this.state.grey = 0;

    this.telemetry.floorStart(gen.depth, gen.spec.id, this.state.contract, this.state.zones, this.bind);
    this.state.phase = 'playing';
    // Set before the opening enemy is placed: `place` reads it.
    this.state.depth = gen.depth;
    if (gen.depth === 1) this.openWith();
    this.state.depth = gen.depth;
    this.state.enemiesAlive = 0;
    this.say(gen.depth === 1 ? 'Clear the floor to open the way out' : `Floor ${gen.depth}`, 3.5);
    // What the floor wants, said once in words and thereafter by the map.
    if (this.state.contract !== 'sweep' || gen.depth > 1) {
      this.sayLater(OPENING[this.state.contract], 3.6, 3.2);
    }
    // Name the fight. A floor whose pairing you can say in a sentence is one
    // you can learn to beat rather than one you happen to survive.
    if (this.bind) this.sayLater(this.bind, 7.2, 3.6);
  }

  /** Put a pickup on the floor, with the weapon's own model if it is one. */
  private dropPickup(kind: string, variant: string | undefined, at: [number, number, number]): void {
    /*
     * All one colour: the map's.
     *
     * Green health, yellow ammo, blue armour and orange weapons is four
     * saturated hues on the floor of a world that allows itself one, and none
     * of them survives being colour-blind or being forty metres away anyway.
     * What tells you which is which is the shape standing over the ring — a
     * gun is the gun, and the rest carry their own marker — so the light is
     * free to say only "something is here".
     */
    const colour = accentFor(this.gen?.spec.id ?? 'descent');
    const g = buildPickup(kind, colour, at);
    g.userData.variant = variant;
    this.root.add(g);
    this.pickups.push(g);
    if (kind === 'weapon' && variant) {
      const w = weaponById(variant);
      // Parse and compile it now, while it is a thing on the floor across the
      // room, rather than on the frame somebody walks onto it.
      this.prefetch?.(w.model);
      // At true scale. The beam and halo are what make it visible from range;
      // the gun itself only has to be the right gun.
      // With its class stripe, so what is on the floor can be told apart from
      // across the room by the same colour the HUD uses for what you hold.
      if (w.model) attachWeaponModel(g, w.model, familyOf(w.cls).colour);
    }
  }

  private clearFloor(): void {
    for (const e of this.enemies) this.retire(e);
    this.enemies.length = 0;
    this.roster.length = 0;
    for (const p of this.pickups) {
      this.root.remove(p);
      // A weapon on the floor is a freshly parsed model — one per pickup, on
      // purpose, because these are rigged and cloning them is a minefield. A
      // fresh parse per pickup is also a fresh geometry and a fresh material
      // per pickup, and a hundred floors of those is a hundred floors of them.
      disposeTree(p);
    }
    this.pickups.length = 0;
    this.pending.length = 0;
    this.clearSignal();
    for (const p of this.discarded) disposeTree(p);
    this.discarded.length = 0;
    for (const p of this.projectiles) this.root.remove(p.mesh);
    this.projectiles.length = 0;
    this.fx.clear();
  }

  dispose(): void {
    this.clearFloor();
    this.scene.remove(this.root);
  }

  /**
   * Put one of every type in a row in front of the player. Only used from the
   * console while tuning; silhouettes are the thing this game asks the most of
   * and they need to be looked at side by side.
   */
  parade(types: EnemyType[]): string[] {
    const cam = this.player.camera.position;
    const yaw = this.player.yaw;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const rx = Math.cos(yaw);
    const rz = -Math.sin(yaw);
    for (const e of [...this.enemies]) {
      this.enemies.splice(this.enemies.indexOf(e), 1);
      this.retire(e);
    }
    types.forEach((t, i) => {
      const e = this.acquire(t);
      const off = (i - (types.length - 1) / 2) * 2.2;
      e.health = 1e9;
      e.dead = false;
      e.cooldown = 1e9;
      e.pos.set(cam.x + fx * 9 + rx * off, this.player.pos.y + (t.hover ?? 0), cam.z + fz * 9 + rz * off);
      e.group.position.copy(e.pos);
      e.group.rotation.y = yaw + Math.PI;
      e.group.visible = true;
      this.enemies.push(e);
    });
    return types.map((t) => t.id);
  }

  /**
   * The nearest enemy with a clear line to it, or null.
   *
   * Distinct from `nearestTarget`, which returns the closest one whether or
   * not there is a wall in between. The practice bot needs the difference to
   * decide between shooting and moving; without it, it will happily empty a
   * magazine into the cover an enemy is standing behind.
   */
  visibleTarget(): { at: THREE.Vector3; armoured: boolean } | null {
    const cam = this.player.camera.position;
    let best: Enemy | null = null;
    let bestD = Infinity;
    for (const e of this.enemies) {
      const aim = new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.55, e.pos.z);
      const d = aim.distanceTo(cam);
      if (d >= bestD || !this.canSee(aim, cam)) continue;
      bestD = d;
      best = e;
    }
    if (!best) return null;
    return {
      at: new THREE.Vector3(best.pos.x, best.pos.y + best.type.height * 0.55, best.pos.z),
      // Whether shooting it from here is worth anything, which decides whether
      // the right move is to keep firing or to get around the side of it.
      armoured: (best.type.frontArmour ?? 1) < 0.6,
    };
  }

  /**
   * The cone the next round goes into, right now.
   *
   * Pulled out of `fire` so the reticle can ask the same question the bullet
   * does. Anything else — a reticle that opens on a timer, or on a count of
   * shots it keeps itself — eventually disagrees with where the rounds go,
   * and a sight that lies is worse than no sight.
   */
  private liveSpread(): number {
    const w = this.state.weapon;
    const steady = this.stillFor > 0.5 ? this.mods.deadeye : 1;
    const bloom = this.hasEarned('slamfire') ? Math.max(w.bloom, 0.4) * 3 : w.bloom;
    const heat = 1 + bloom * Math.min(1, this.burst / 14);
    return w.spread * this.mods.spread * steady * heat;
  }

  /** Upload anything the effects need before it is first wanted mid-fight. */
  warm(renderer: THREE.WebGLRenderer): void {
    this.fx.warm(renderer);
  }

  /** Where the way out currently is, once it has opened. */
  exitPosition(): THREE.Vector3 {
    return this.exitPos.clone();
  }

  /**
   * The nearest living enemy's aim point, or null. Used by the practice bot in
   * `tools/sim.ts` to play the game the way a person does — the balance
   * numbers are only worth anything if the thing being measured fights back.
   */
  nearestTarget(): THREE.Vector3 | null {
    const cam = this.player.camera.position;
    let best: Enemy | null = null;
    let bestD = Infinity;
    for (const e of this.enemies) {
      const d = e.pos.distanceTo(cam);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best ? new THREE.Vector3(best.pos.x, best.pos.y + best.type.height * 0.55, best.pos.z) : null;
  }

  /** Where the way out is, once there is one, for anything that wants to point at it. */
  get exitSound(): [number, number, number] | null {
    return this.state.exitOpen ? [this.exitPos.x, this.exitPos.y, this.exitPos.z] : null;
  }

  say(text: string, seconds = 2.2): void {
    this.state.message = text;
    this.state.messageFor = seconds;
  }

  /**
   * Say something after the current line has had its turn.
   *
   * There is one message slot, so two things said at once means one of them
   * never appeared. Queued rather than dropped: the floor number and what the
   * floor is are both worth reading, in that order.
   */
  private pending: { text: string; seconds: number; at: number }[] = [];
  sayLater(text: string, delay: number, seconds = 2.2): void {
    this.pending.push({ text, seconds, at: this.state.elapsed + delay });
    this.pending.sort((a, b) => a.at - b.at);
  }

  // -------------------------------------------------------------------------

  applyCard(card: Card): void {
    const before = this.mods.maxHealth;
    applyEffects(this.mods, card.effects);
    // Health is a number and also a maximum, so the two have to move together
    // or a card that halves the maximum leaves you standing above it.
    this.mods.maxHealth = Math.max(20, Math.round(this.mods.maxHealth));
    this.state.cards.push(card.id);
    this.telemetry.took(card.id);
    this.state.maxHealth = this.mods.maxHealth;
    this.state.health = Math.min(this.mods.maxHealth, this.state.health + Math.max(0, this.mods.maxHealth - before));
    this.player.speedScale = this.mods.moveSpeed;
  }

  startRun(seed = ''): void {
    this.telemetry.run(seed);
    this.mods = baseModifiers();
    Object.assign(this.state, {
      phase: 'playing' as Phase,
      depth: 1,
      score: 0,
      kills: 0,
      health: this.mods.maxHealth,
      maxHealth: this.mods.maxHealth,
      weapon: STARTER,
      carried: [STARTER.id],
    mags: { [STARTER.id]: STARTER.magazine },
    stock: { [STARTER.id]: STARTER.pickupAmmo },
      cards: [],
      ammo: STARTER.magazine,
      reserve: STARTER.pickupAmmo,
      reloading: false,
      elapsed: 0,
      killedBy: '',
    });
    this.player.speedScale = 1;
  }

  /**
   * Does the weapon in hand play by this rule?
   *
   * Intrinsic rules come off the action and are free. Earned ones come off a
   * card and are gated on a tag, so a card about pump actions does nothing at
   * all while you are holding a rifle — which is the point: it is a reason to
   * care what the next weapon on the floor is.
   */
  private has(rule: Trait): boolean {
    return INTRINSIC[this.state.weapon.action].trait === rule || this.hasEarned(rule);
  }

  private hasEarned(rule: Trait): boolean {
    if (!this.mods.traits[rule]) return false;
    const w = this.state.weapon;
    switch (rule) {
      case 'slamfire': return w.action === 'pump';
      case 'battle_rhythm': return w.era === 'wartime';
      case 'deep_freeze': return w.era === 'cold';
      default: return true;
    }
  }

  private magOf(w: Weapon): number {
    return Math.max(1, Math.round(w.magazine * this.mods.magazine));
  }

  reload(): void {
    const s = this.state;
    if (s.reloading || s.ammo >= this.magOf(s.weapon) || s.reserve <= 0) return;
    s.reloading = true;
    this.burstLeft = 0;
    this.reloadTimer = s.weapon.reload / this.mods.reloadSpeed;
    sound.clack(0.85, 0.35);
    /*
     * The magazine you throw away.
     *
     * Thrown forward rather than dropped, because a grenade at your own feet
     * is a card that kills you — and thrown *on the reload*, which means the
     * card's real effect is that reloading becomes an attack. Reloading into a
     * room rather than out of one is a decision nobody was making before.
     */
    if (this.hasEarned('mag_grenade')) {
      const cam = this.player.camera;
      const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
      const at = cam.position.clone().addScaledVector(fwd, 2.6).setY(cam.position.y - 0.6);
      this.blast(at, 30, 4.2, 0.55);
    }
  }

  /**
   * Something goes off, hurting whatever is near it — and you, if you are.
   *
   * One implementation for every explosion the player owns: the thrown
   * magazine, the third shell, the point-blank break action. Self-damage is
   * reduced rather than removed, because an explosion you can stand in is not
   * one you have to think about, and thinking about it is the whole cost of
   * carrying these.
   */
  private blast(at: THREE.Vector3, damage: number, radius: number, selfShare: number): void {
    this.fx.burst(at, accentFor(this.gen?.spec.id ?? 'descent'), 22);
    sound.blast([at.x, at.y, at.z]);
    for (const e of [...this.enemies]) {
      const d = e.pos.distanceTo(at);
      if (d < radius) this.damage(e, damage * (1 - d / radius));
    }
    const mine = at.distanceTo(this.player.camera.position);
    if (selfShare > 0 && mine < radius) {
      this.hurt(damage * selfShare * (1 - mine / radius), at, 'your own ordnance');
    }
  }

  swap(): void {
    const s = this.state;
    if (s.carried.length < 2) return;
    // Stow what is in the one you are putting down, and take out the other
    // exactly as you left it — magazine and spares both.
    s.mags[s.weapon.id] = s.ammo;
    s.stock[s.weapon.id] = s.reserve;
    const i = s.carried.indexOf(s.weapon.id);
    s.weapon = weaponById(s.carried[(i + 1) % s.carried.length]);
    s.ammo = s.mags[s.weapon.id] ?? this.magOf(s.weapon);
    s.reserve = s.stock[s.weapon.id] ?? s.weapon.pickupAmmo;
    s.reloading = false;
    // Whatever the last gun still owed you, the new one does not.
    this.burstLeft = 0;
    this.burst = 0;
    sound.clack(1.15, 0.35);
    this.say(s.weapon.name, 1.4);
  }

  // -------------------------------------------------------------------------

  update(dt: number): void {
    const s = this.state;
    // Bodies lie on the world's floor, and are shoved by anyone who walks
    // through them. Rebound each frame because the collider set changes floor
    // to floor and a stale closure would drop them through the new one.
    this.fx.groundAt = (x, z, y) => this.floorUnder(x, z, y);
    this.fx.disturb(this.player.pos);
    /*
     * Spend the held time before anything else sees the frame.
     *
     * The whole frame slows, not just the enemy — the point is that the moment
     * of contact lasts longer, and a world that keeps moving while the thing
     * you shot does not is a stutter rather than a punch. The effects keep
     * their own clock, so the shards and the muzzle core do not freeze with
     * it and the flash still reads as instantaneous.
     */
    const real = dt;
    if (this.freeze > 0) {
      const held = Math.min(this.freeze, dt);
      this.freeze -= held;
      dt = Math.max(0, dt - held);
    }
    // One freed object per frame, never on the frame it was collected.
    const junk = this.discarded.shift();
    if (junk) disposeTree(junk);

    s.hitFlash = Math.max(0, s.hitFlash - dt * 3);
    s.damageFlash = Math.max(0, s.damageFlash - dt * 1.7);
    s.pickupFlash = Math.max(0, s.pickupFlash - dt * 2.5);
    s.messageFor = Math.max(0, s.messageFor - dt);
    // On the real clock: a muzzle core lasts forty milliseconds and a hit
    // freeze is about that long, so sharing a clock would leave the flash
    // hanging in the air for exactly the shot that earned the freeze.
    this.fx.update(real);
    this.spin(dt);
    if (s.phase !== 'playing') return;
    s.elapsed += dt;

    if (s.reloading) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        const want = this.magOf(s.weapon) - s.ammo;
        const take = Math.min(want, s.reserve);
        s.ammo += take;
        s.reserve -= take;
        s.mags[s.weapon.id] = s.ammo;
        s.stock[s.weapon.id] = s.reserve;
        s.reloading = false;
        this.sinceLoad = 0;
        sound.clack(1.35, 0.4);
      }
    }

    // Acquire once per frame: the same target steers the assist and, when the
    // player pulls the trigger, bends the shot. It never pulls the trigger for
    // them — a gun that fires on its own reads as a bug, and the whole point
    // of aiming is that it is the thing you are doing.
    const target = this.aim();
    this.assist(dt, target);
    const onTarget = target !== null && target.angle <= Math.atan2(target.e.type.radius, target.dist) + FIRE_CONE;

    this.fireTimer -= dt;
    /*
     * Held time, for the automatics that wind up.
     *
     * Reset by letting go rather than by firing, so tapping an automatic
     * throws away everything it had spun up — which is the trade the rule is
     * making: the fastest this gun gets is the reward for committing to a
     * burst you cannot immediately take back.
     */
    this.heldFor = this.triggerHeld ? this.heldFor + dt : 0;
    if (this.canter > 0) {
      this.canter = Math.max(0, this.canter - dt);
      if (this.canter === 0) this.player.speedScale = this.mods.moveSpeed;
    }
    const slam = this.hasEarned('slamfire');
    const auto = s.weapon.action === 'auto' || slam;
    // A burst is bought by one pull and delivered whatever the trigger does
    // next, which is the whole reason to hold one.
    const wantsToFire = this.burstLeft > 0 || (auto ? this.triggerHeld : this.triggerPressed);
    const fresh = this.triggerPressed;
    this.triggerPressed = false;
    if (wantsToFire && !s.reloading && this.fireTimer <= 0) {
      if (s.ammo > 0) {
        this.fire(onTarget ? target : null);
        if (CYCLED_BY_HAND.has(s.weapon.action) && this.mods.instantCycle[s.weapon.action] !== true) {
          // The bolt or the pump, half a beat after the round leaves.
          setTimeout(() => sound.clack(1.0, 0.3), Math.min(400, s.weapon.interval * 380));
        }
        /*
         * A bolt somebody else worked for you.
         *
         * `chamber` is what makes a bolt action worth carrying past floor
         * four: the gun's whole cost is the second between rounds, and hitting
         * a head removes it. So the reward for the hardest shot in the game is
         * that you get to take the next one immediately, which is a much
         * better rule than "bolt actions deal more damage".
         */
        const cycles = this.mods.instantCycle[s.weapon.action] === true || this.chambered;
        this.chambered = false;
        let interval = cycles ? Math.min(0.16, s.weapon.interval * 0.3) : s.weapon.interval;
        // Slamfire: as fast as the hand can work it, which on a pump is fast.
        if (slam) interval *= 0.42;
        if (s.weapon.action === 'burst') {
          if (this.burstLeft > 0) this.burstLeft--;
          else if (fresh) this.burstLeft = Math.max(0, s.weapon.burst - 1);
          // Inside the burst the gun runs at its mechanical rate; between
          // bursts it waits, which is what stops a burst gun being an
          // automatic one with extra steps.
          interval = this.burstLeft > 0 ? interval * 0.34 : interval * 1.5;
        }
        // A streak is a reason to keep pushing rather than to back off, which
        // is the opposite of what most damage cards encourage.
        const rush = 1 + this.mods.momentum * this.streak;
        // And an automatic that has been held gets faster the longer it is.
        const wound = this.has('spinup')
          ? 1 + SPINUP_GAIN * Math.min(1, this.heldFor / SPINUP_TIME)
          : 1;
        this.fireTimer = interval / (this.mods.fireRate * rush * wound);
        /*
         * A lever worked on the move carries you.
         *
         * The one rule here about the player's feet rather than their trigger,
         * and it is on the lever action on purpose: a lever gun is slow, so
         * the thing it can offer is that using it is not a reason to stop.
         */
        if (this.has('canter') && (this.player.analog?.y ?? 0) > 0.4) {
          this.canter = 1.1;
          this.player.speedScale = this.mods.moveSpeed * 1.32;
        }
      } else if (s.reserve > 0) {
        this.burstLeft = 0;
        this.reload();
      } else {
        this.burstLeft = 0;
        sound.dry();
        this.fireTimer = 0.35;
      }
    }

    /*
     * Recovery starts on the next frame, always, and eases rather than ramps.
     *
     * It used to wait out a settle window sized from the weapon's own cycle
     * time, so that recovery could not fire between two rounds and eat the
     * climb. What that actually produced was a reticle that went up and stayed
     * up for the length of a burst and then slid back down afterwards — the
     * player never sees a shot return, only a slow drift they have to fight
     * with the mouse, and the drift arrives late enough that it feels like the
     * game moving their aim rather than the gun settling.
     *
     * Immediate exponential recovery gives the shape a gun actually has: the
     * reticle snaps up on the round and is most of the way home before the
     * next one. Sustained fire still climbs, because each round adds a whole
     * kick and only a fraction comes back before the following one — the climb
     * is now a plateau the pattern converges on rather than a ramp without a
     * ceiling, which is both more learnable and more honest about what
     * holding the trigger costs.
     */
    this.sinceShot += dt;
    if (this.recoilOwed > 1e-4 || this.swayOwed !== 0) {
      const keep = Math.exp(-RECOIL_RETURN * dt);
      const give = this.recoilOwed * (1 - keep);
      this.player.pitch -= give;
      this.recoilOwed -= give;
      // The drift comes back on the same curve, so the reticle ends where it
      // started rather than a few degrees to one side of it.
      const back = this.swayOwed * (1 - keep);
      this.player.yaw -= back;
      this.swayOwed -= back;
      // Below a fiftieth of a degree there is nothing left to see, and letting
      // an exponential run forever leaves the aim permanently a hair off.
      if (this.recoilOwed < 3e-4 && Math.abs(this.swayOwed) < 3e-4) {
        this.player.pitch -= this.recoilOwed;
        this.player.yaw -= this.swayOwed;
        this.recoilOwed = 0;
        this.swayOwed = 0;
      }
    }
    // The pattern resets once the gun is genuinely at rest, which is a fixed
    // moment now rather than one that moved with whatever you were holding.
    if (!this.triggerHeld && this.sinceShot > 0.32) this.burst = 0;
    s.recoil = this.recoilOwed;
    s.spread = this.liveSpread();

    /**
     * Falling repeatedly means the kill plane is papering over a hole.
     *
     * The plane puts the player back where they were last standing, which is
     * the right answer for a one-off — but if that spot is itself next to the
     * hole, they fall again immediately and the run becomes a loop of being
     * rescued. A burst of rescues means the local ground cannot be trusted, so
     * they go back to the start of the floor, which is known good.
     */
    this.rescueWindow = Math.max(0, this.rescueWindow - dt);
    if (this.player.rescues > this.rescueWatch) {
      const burst = this.rescueWindow > 0;
      this.rescueWatch = this.player.rescues;
      this.rescueWindow = 4;
      if (burst) {
        this.player.teleport(this.spawnPoint);
        this.rescueWindow = 0;
        this.say('Bad footing — back to the start', 2);
      }
    }

    this.iFrames = Math.max(0, this.iFrames - dt);
    this.sinceKill += dt;
    this.lastDt = dt;
    const feet = fromThree(this.player.pos);
    this.telemetry.tick(dt, this.enemies.length, feet);
    this.telemetry.holding(s.weapon.name, dt);
    this.sinceDamage += dt;
    /*
     * What is owed drains once the fight has moved on.
     *
     * The window is deliberately short. Recoverable health is a reason to go
     * forward *now*, and one you can collect two rooms later is just delayed
     * regeneration with extra steps.
     */
    if (this.greyFor > 0) {
      this.greyFor -= dt;
    } else if (this.grey > 0) {
      this.grey = Math.max(0, this.grey - GREY_DRAIN * dt);
    }
    /*
     * And a floor, so being unable to kill anything is not a dead end.
     *
     * It rises with depth, because a fixed forty stops being a floor. Measured
     * over seventy runs: floor five costs 23 health, floor nine costs 70, and
     * floor eleven 94 — so the same forty that is most of a life on floor one
     * is half a floor's damage by the time it matters. The clear rate showed
     * it exactly there, holding at 100/91/88/88 through floor eight and then
     * falling off a cliff to 61% on nine, which is not a floor that got harder
     * so much as a run that had run out of room. Nothing about any individual
     * fight changes here; what changes is how much of the last one you are
     * still carrying when the next starts.
     */
    const rest = Math.min(
      s.maxHealth,
      Math.max(REST_HEALTH, s.maxHealth * (0.4 + Math.min(0.25, (s.depth - 1) * 0.03))),
    );
    if (this.sinceDamage > 6 && s.health < rest) s.health = Math.min(rest, s.health + 5 * dt);
    s.grey = Math.min(this.grey, s.maxHealth - s.health);

    this.updateTempo(dt);
    this.updateMessages();
    this.updateSpawning(dt);
    if (this.flow) {
      const feet = this.player.pos;
      const canon = fromThree(new THREE.Vector3(feet.x, feet.y, feet.z));
      this.flow.update(dt, canon[0], canon[1], canon[2] + 0.1);
    }
    this.updateEnemies(dt);
    this.updateProjectiles(dt);
    this.updatePickups();
    this.updateExit();
    s.enemiesAlive = this.enemies.length;
  }

  // -------------------------------------------------------------------------

  /** Streak decay, and how long the player has held still. */
  private updateTempo(dt: number): void {
    if (this.streakFor > 0) {
      this.streakFor -= dt;
      if (this.streakFor <= 0) this.streak = 0;
    }
    const speed = Math.hypot(this.player.velocity.x, this.player.velocity.z);
    this.stillFor = speed < 0.5 ? this.stillFor + dt : 0;
  }

  private updateMessages(): void {
    const next = this.pending[0];
    if (next && this.state.elapsed >= next.at) {
      this.pending.shift();
      this.say(next.text, next.seconds);
    }
  }

  private updateSpawning(dt: number): void {
    if (!this.gen || !this.graph) return;
    const shape = waveShape(this.state.depth);
    const d = this.director;
    if (!d) {
      // No zones: one undivided floor, which is what this always was.
      if (!this.roster.length || this.enemies.length >= shape.concurrent) return;
      this.spawnTimer -= dt;
      if (this.spawnTimer > 0) return;
      if (this.spawn(this.roster[0], shape, this.spawnMisses >= 3)) {
        this.roster.shift();
        this.spawnMisses = 0;
      } else {
        this.spawnMisses++;
      }
      this.spawnTimer = Math.max(0.35, 1.9 - this.state.depth * 0.09);
      return;
    }

    const feet = this.player.pos;
    const at = fromThree(new THREE.Vector3(feet.x, feet.y, feet.z));
    const wasZone = d.active;
    let standing = 0;
    for (const e of this.enemies) if (e.staged && !e.dead) standing++;
    const beat = d.update(dt, at, shape.grace, standing);
    const s = this.state;
    s.zone = d.active + 1;
    s.hold = d.contract === 'hold' ? d.holdProgress : -1;
    /*
     * The signal ticks, faster as it fills.
     *
     * Audible rather than visual because standing in the signal is the one
     * time the player must not be looking at the HUD — they are surrounded, by
     * design, and the bar is at the top of the screen. A rising interval says
     * "nearly" without asking for a glance.
     */
    if (s.hold > 0 && s.hold < 1) {
      this.holdTick -= dt;
      if (this.holdTick <= 0) {
        sound.signal(s.hold);
        this.holdTick = 1.1 - s.hold * 0.75;
      }
    } else {
      this.holdTick = 0;
    }
    if (d.active !== wasZone) {
      // Only worth saying on a sweep — on a hunt or a hold the zone is a
      // staging detail and the objective has not changed.
      if (d.contract === 'sweep') this.say(`Zone ${d.active + 1} of ${d.zones.length}`, 1.8);
      this.spawnMisses = 0;
    }
    if (!beat.release) return;
    /*
     * A release the floor cannot afford is postponed, not lost.
     *
     * The concurrency cap and a failed placement are both handled by simply
     * not taking the enemy off the wave: the director's own interval fires
     * again shortly and tries the same one. Nothing is dropped, which is what
     * lets the roster be a promise the seed made rather than an intention.
     */
    if (this.enemies.length >= shape.concurrent) return;
    const type = d.peek();
    if (!type) return;
    /*
     * The floor's stand-out enemy.
     *
     * A hunt always has one, because the contract is about it. Any other
     * contract has one only when the floor drew an elite rule — in which case
     * the toughest thing in the last zone wears it, which is where the fight
     * was going to peak anyway.
     */
    const mark = d.nextIsMark || (this.elite !== null && !this.quarry && d.atMarkSlot);
    // After a few failures, take any legal spot rather than a good one. A
    // floor that will not populate is a floor that cannot be finished.
    if (this.spawn(type, shape, this.spawnMisses >= 3, d.zones[beat.zone], mark)) {
      d.take();
      this.spawnMisses = 0;
    } else {
      this.spawnMisses++;
    }
  }

  /**
   * Floor one starts with something already standing in front of you.
   *
   * Every other enemy in the game arrives out of sight, which is a rule worth
   * having and which on the first floor costs fourteen seconds — measured —
   * before the player has seen a single hostile. Six of grace, then an arrival
   * fourteen metres away behind something, then a walk. A first floor that
   * takes a quarter of a minute to show you what the game is about has
   * already lost the person it was written for.
   *
   * This is not a spawn. It is the level's opening state: the thing is on the
   * map when the map appears, ahead of you, far enough away to be a sight
   * rather than a threat. Nothing materialises in your face, which is the
   * whole of what the rule was protecting.
   */
  private openWith(): void {
    const d = this.director;
    const type = d ? d.peek() : this.roster[0];
    const gen = this.gen;
    if (!type || !gen?.enemyPosts.length) return;
    /*
     * The player's feet, not the camera.
     *
     * `Player.teleport` moves `pos`; the camera is only dragged along inside
     * `Player.update`, which has not run yet. Measuring from the camera here
     * measures from wherever the player was standing on the *previous* floor,
     * which is why the first two attempts at this placed nothing at all and
     * the number in the report did not move.
     */
    const feet = this.player.pos;
    const cam = new THREE.Vector3(feet.x, feet.y + 1.6, feet.z);

    /*
     * Ahead if possible, visible at all if not.
     *
     * Two passes rather than one set of constraints, because the tight version
     * found a spot on one map in six: the posts are placed to be *away* from
     * the player, so a spawn point with a clear line to the arrival point and
     * inside a sixty-degree cone is a coincidence rather than a guarantee. A
     * hostile behind you at twelve metres on floor one is still a hostile you
     * see within a second of looking around, and looking around is the first
     * thing anybody does.
     */
    const fx = -Math.sin(this.player.yaw);
    const fz = -Math.cos(this.player.yaw);
    let best: [number, number, number] | null = null;
    let bestScore = -Infinity;
    for (const p of gen.enemyPosts) {
      const t = toThree(p[0], p[1], p[2]);
      const dist = Math.hypot(t.x - cam.x, t.z - cam.z);
      if (dist < 9 || dist > 34) continue;
      const eye = new THREE.Vector3(t.x, t.y + 1.2, t.z);
      /*
       * Ahead, measured off the player's yaw rather than off the camera.
       *
       * The camera's quaternion is only rebuilt inside `Player.update`, and
       * this runs during `enter`, before a single frame has been stepped — so
       * `inView` here is asking about the direction the player was facing on
       * the *previous* floor. Which is why the first version of this placed
       * nothing at all and the measurement did not move.
       */
      if (!this.canSee(eye, cam)) continue;
      const ahead = ((t.x - cam.x) * fx + (t.z - cam.z) * fz) / Math.max(1e-4, dist);
      // Close and in front beats far and behind, and both beat nothing.
      const score = ahead * 12 - Math.abs(dist - 17);
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    if (!best) return;

    const shape = waveShape(1);
    const e = this.acquire(type);
    const t = toThree(best[0], best[1], best[2]);
    e.pos.copy(t);
    if (type.hover) e.pos.y += type.hover;
    e.type = type;
    e.health = Math.round(type.health * shape.healthScale);
    e.peak = e.health;
    e.dead = false;
    e.stuckFor = 0;
    e.bestDist = Infinity;
    e.node = -1;
    e.cooldown = 1.4;
    e.slow = 1;
    e.bob = this.rand() * 6.28;
    e.marked = false;
    e.elite = null;
    e.returned = false;
    e.staged = !!d;
    e.group.scale.setScalar(1);
    e.group.visible = true;
    e.group.position.copy(e.pos);
    // No warmup and no arrival light: it was always here.
    e.warmup = 0;
    fade(e, 1);
    e.glow = null;
    this.enemies.push(e);
    if (d) d.take();
    else this.roster.shift();
  }

  private spawn(
    type: EnemyType,
    shape: ReturnType<typeof waveShape>,
    anywhere = false,
    zone?: Zone,
    mark = false,
  ): boolean {
    const gen = this.gen!;
    const cam = this.player.camera.position;
    /*
     * The live zone's own posts, when it has enough of them to choose from.
     *
     * This is what makes a zone a place rather than a label: everything the
     * fight is made of arrives from inside the piece of map you are standing
     * in, so clearing it means the ground behind you stays clear. A zone with
     * only a handful of legal posts falls back to the whole map — better an
     * enemy from the wrong end than a floor that cannot populate.
     */
    const posts = !anywhere && zone && zone.posts.length >= 4 ? zone.posts : gen.enemyPosts;
    if (!posts.length) return false;

    /*
     * Bias toward where you are being sent.
     *
     * Enemies arriving from the direction you are already being told to walk
     * means pushing forward and fighting are the same action — which is the
     * whole lesson a first floor has to teach, and it stops the game feeling
     * like an ambush from behind that nobody could have seen coming. Once
     * there are zones the target is the middle of the live one rather than
     * the way out, which is the same idea measured against the fight you are
     * in rather than the end of the floor.
     */
    const aim = zone ? toThree(zone.centre[0], zone.centre[1], zone.centre[2]) : this.exitPos;
    const toward = new THREE.Vector3(aim.x - cam.x, 0, aim.z - cam.z);
    const wantDir = toward.lengthSq() > 1 ? toward.normalize() : null;

    let best: [number, number, number] | null = null;
    let bestScore = -Infinity;
    for (let i = 0; i < 24; i++) {
      const p = posts[Math.floor(this.rand() * posts.length)];
      const t = toThree(p[0], p[1], p[2]);
      const dx = t.x - cam.x;
      const dz = t.z - cam.z;
      const d = Math.hypot(dx, dz);
      /*
       * Close enough to walk in from, far enough not to appear on top of you.
       *
       * The relaxed pass used to allow six metres. Six metres is the width of
       * a room: out of the frustum at the moment it is chosen, and directly in
       * front of you the instant you turn your head — which is what "it
       * spawned right in front of me" means, and it is not answered by the
       * off-screen test because that test was true when it was asked. Eleven
       * is roughly the point at which arriving reads as walking in.
       */
      if (anywhere ? d < 11 : d < 14 || d > 62) continue;
      /*
       * Nothing arrives where you are looking.
       *
       * Two ways to be out of sight and either will do: behind a solid, or
       * simply off the edge of the screen. Being off-screen is the one that
       * always has candidates — a room with no cover still has a behind-you —
       * which is what lets this be a rule rather than a preference. It used to
       * be worth a bonus of 1.2 against a scoring range of about 2, so a spot
       * in plain view could and did win.
       */
      const eye = new THREE.Vector3(t.x, t.y + 1.2, t.z);
      const onScreen = this.inView(eye);
      if (onScreen && this.canSee(eye, cam)) {
        // Last resort, after the good spots and then the legal ones have both
        // run out: far enough away that it is scenery rather than an ambush.
        if (!anywhere || d < 30) continue;
      }
      const alignment = wantDir ? (dx * wantDir.x + dz * wantDir.z) / d : 0;
      const score = (onScreen ? 0 : 1.2) + alignment + this.rand() * 0.5 - Math.abs(d - 26) * 0.012;
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    if (!best) return false;

    const e = this.acquire(type);
    const t = toThree(best[0], best[1], best[2]);
    e.pos.copy(t);
    if (type.hover) e.pos.y += type.hover;
    e.type = type;
    e.health = Math.round(type.health * shape.healthScale);
    /*
     * The marked one, on a hunt.
     *
     * A little larger, and the fight rather than a normal enemy wearing a hat
     * — but the same behaviour, the same telegraphs and the same counter, so
     * everything the player has learned still applies.
     */
    e.marked = mark;
    e.staged = !!this.director;
    /*
     * One rule per floor — but not the resurrection one on something you
     * already have to walk around.
     *
     * An elite rule is supposed to add a decision. Revenant on a Bulwark adds
     * none: the counter to front armour is to get round the side of it, the
     * counter to a revenant is to burst it through the threshold, and doing
     * both, twice, is not a harder decision — it is the same one for twice as
     * long. Measured, a Revenant Bulwark was 13% of floor seven's damage by
     * itself, on a floor that cleared at 78% between two at 94%.
     *
     * Brood instead, chosen from the same measurement rather than by taste:
     * on that floor a Brood Bulwark cost 8.1% against the Revenant's 13.2%,
     * and what it asks — mind where you are standing when it finally goes
     * down — is a different question from the one the armour already asked.
     *
     * Not Siphon, which looked like the safest of the four and is not: its
     * drain never kills, so an armoured mark carrying it would be a tank you
     * cannot lose to.
     */
    const rule = mark ? this.elite : null;
    /*
     * Nor on something whose whole threat is a single event.
     *
     * A Popper's answer is to back off and kill it before it arrives. Coming
     * back once does not complicate that, it simply happens twice — the same
     * explosion, for the same reason, and a Revenant Popper was doing a tenth
     * of floor seven's total damage on its own.
     */
    const compounds = (type.frontArmour ?? 1) < 0.6 || (type.detonates ?? 0) > 0;
    e.elite = rule === 'revenant' && compounds ? 'brood' : rule;
    e.returned = false;
    if (mark) {
      /*
       * A fixed amount of fight, not a multiple of whatever got marked.
       *
       * It used to be a flat 2.5x, which meant the boss was however tough the
       * thing that drew the short straw happened to be. Measured against the
       * starter — the gun a run carries two thirds of the time — a marked
       * Spitter on floor three was 0.7 of a magazine and a marked Brute on
       * floor thirteen was 6.6. One of those is not a boss and the other is an
       * endurance test, and the hunt contract cleared at 80% against 94% for
       * both of the others because of it.
       *
       * Worse, it got there by accident: the multiplier was set when every
       * enemy had two and a half times less health than it does now, so
       * raising the table raised the boss with it and pushed the heavy types
       * past the point where you reload three times mid-fight.
       *
       * So the mark now has a budget of its own that scales with the floor,
       * and the type only raises it from there — a marked Brute is still
       * heavier than a marked Grunt, just not three magazines heavier. It
       * lands between one magazine on floor one and four on floor thirteen,
       * which is a fight you have to commit to and can still finish.
       */
      /*
       * Except on a bomber, where health is the wrong lever entirely.
       *
       * A Popper's threat is arriving, and hit points are precisely what stops
       * it arriving. The budget above turned a 117-point suicide charge into a
       * thousand-point one that cannot be shot down on the way in — which is
       * not a boss, it is an unavoidable explosion. It keeps its own scale.
       */
      const bomb = (type.detonates ?? 0) > 0;
      const own = e.health * 1.5;
      const budget = MARKED_HEALTH * shape.healthScale;
      const worth = bomb ? e.health * 1.8 : Math.max(own, budget);
      e.health = Math.round(worth * (e.elite ? ELITES[e.elite].health / 2 : 1));
      e.group.scale.setScalar(1.28);
      this.quarry = e;
      const rule = e.elite ? ELITES[e.elite] : null;
      this.say(rule ? `${rule.name} sighted` : 'Target sighted', 2.4);
      // The rule once per run, the same as a weapon's. After that the name is
      // enough: a player who has met a Revenant knows what a Revenant does.
      if (e.elite && !this.metElites.has(e.elite)) {
        this.metElites.add(e.elite);
        this.sayLater(rule!.text, 2.5, 3.2);
      }
      sound.spotted([e.pos.x, e.pos.y, e.pos.z]);
    } else {
      e.group.scale.setScalar(1);
    }
    e.peak = e.health;
    e.dead = false;
    e.stuckFor = 0;
    e.bestDist = Infinity;
    e.node = -1;
    e.cooldown = 0.8;
    e.slow = 1;
    e.bob = this.rand() * 6.28;
    e.group.visible = true;
    e.group.position.copy(e.pos);
    // Arriving: still, see-through, and lit, for one second.
    e.warmup = WARMUP;
    fade(e, 0);
    e.glow = this.fx.claimSpawnLight();
    if (e.glow) e.glow.position.set(e.pos.x, e.pos.y + type.height * 0.6, e.pos.z);
    sound.spawned([e.pos.x, e.pos.y, e.pos.z]);
    this.enemies.push(e);
    return true;
  }

  /**
   * Is this point anywhere on the player's screen?
   *
   * A cone around the view direction wide enough to contain the frustum, so
   * "not in view" is never a lie in the direction that matters — a corner of
   * the screen counts as being looked at.
   */
  private inView(at: THREE.Vector3): boolean {
    const cam = this.player.camera;
    const to = at.clone().sub(cam.position);
    const len = to.length();
    if (len < 1e-4) return true;
    to.divideScalar(len);
    const fwd = cam.getWorldDirection(new THREE.Vector3());
    const half = Math.atan(Math.tan((cam.fov * Math.PI) / 360) * Math.max(1, cam.aspect));
    // The frustum's corners sit further out than its edges, so the cone that
    // contains it is wider than the horizontal half-angle by the diagonal.
    /*
     * A margin past the corners, because the player turns.
     *
     * This answers "is it on screen now", and the thing being decided with it
     * is where something should arrive — which it will still be doing a moment
     * later, by which time "now" has moved. A fifth again beyond the diagonal
     * covers about a quarter-second of an ordinary turn.
     */
    return to.dot(fwd) > Math.cos(Math.min(Math.PI * 0.49, half * 1.75));
  }

  /** Colliders whose cell the point falls in, plus the ring around it. */
  private near(x: number, z: number): Collider[] {
    const out: Collider[] = [];
    const cx = Math.floor(x / Run.CELL);
    const cz = Math.floor(z / Run.CELL);
    for (let i = -1; i <= 1; i++)
      for (let j = -1; j <= 1; j++) {
        const arr = this.cells.get(`${cx + i},${cz + j}`);
        if (arr) out.push(...arr);
      }
    return out;
  }

  /**
   * Move an enemy one axis at a time, pushing out of anything it lands in.
   *
   * Axis-separated so a body that cannot go forward can still go sideways —
   * resolving both at once makes anything touching a wall stop dead. The push
   * is back to the side it came from, for the same reason the player's is: a
   * body standing at the very edge of a room is already inside the wall band,
   * and resolving by direction of travel ejects it through.
   */
  private slide(e: Enemy, dx: number, dz: number): void {
    const t = e.type;
    const r = t.radius * 0.9;
    /*
     * An enemy steps as high as the mesh says anything can.
     *
     * This was `+ 0.25`, which is an enemy step height of twenty-five
     * centimetres — and it was the only place in the game that number
     * appeared. The bake joins samples within `STEP` of each other and routes
     * enemies through those edges, and the player's own step-up is `STEP` too,
     * so the mesh was promising forty-five and two of the three things that
     * believe it could deliver. Anything between the two was a kerb the player
     * walked up and the enemy following them stood at.
     *
     * `mount`-style lifting is not needed here: the height follow after the
     * move puts the enemy on the mesh node under its feet, so ignoring a low
     * box horizontally is the whole of the climb.
     */
    const lo = e.pos.y + STEP;
    const hi = e.pos.y + t.height - 0.1;
    const hits = this.near(e.pos.x + dx, e.pos.z + dz);

    for (const [axis, delta] of [['x', dx], ['z', dz]] as const) {
      if (Math.abs(delta) < 1e-6) continue;
      const before = e.pos[axis];
      e.pos[axis] += delta;
      for (const c of hits) {
        // Furniture is not on the navmesh, so it cannot be allowed to stop
        // something that is following the navmesh. See `Collider.soft`.
        if (c.soft) continue;
        const b = c.box;
        if (b.max.y <= lo || b.min.y >= hi) continue;
        if (b.max.x <= e.pos.x - r || b.min.x >= e.pos.x + r) continue;
        if (b.max.z <= e.pos.z - r || b.min.z >= e.pos.z + r) continue;
        const low = b.min[axis] - r - 1e-3;
        const high = b.max[axis] + r + 1e-3;
        e.pos[axis] = Math.abs(before - low) <= Math.abs(before - high) ? low : high;
      }
    }
  }

  /**
   * Move an enemy to a reachable post near the player.
   *
   * `engage` inverts what makes a good spot. Arriving on a floor, the best
   * post is one out of sight, so the fight starts when they walk into view.
   * Breaking a stall is the opposite problem: the reason the floor cannot end
   * is that nobody can find anybody, and putting them somewhere hidden again
   * just restarts the same wait.
   */
  /**
   * Move an enemy to a post, either to re-engage or to reposition.
   *
   * Every post is considered rather than a couple of dozen sampled ones, and
   * the band it wants — near and visible to re-engage, further and hidden to
   * reposition — is a preference expressed in the score rather than a filter
   * that can reject everything. It used to be a filter, and when the player
   * stood in a corner of a big map where no sampled post fell inside the band
   * this returned without moving anyone. That is the exact case the caller
   * exists to rescue: the floor cannot end, and the one mechanism guaranteeing
   * it can was declining to fire.
   */
  private relocate(e: Enemy, engage = false): void {
    const gen = this.gen;
    if (!gen?.enemyPosts.length) return;
    const cam = this.player.camera.position;
    const want = engage ? 13 : 18;
    let best: [number, number, number] | null = null;
    let bestScore = -Infinity;
    for (const p of gen.enemyPosts) {
      const t = toThree(p[0], p[1], p[2]);
      const d = Math.hypot(t.x - cam.x, t.z - cam.z);
      // Never on top of the player, whatever else is true.
      if (d < 5) continue;
      const seen = this.canSee(new THREE.Vector3(t.x, t.y + 1.2, t.z), cam);
      const score = (seen === engage ? 2 : 0) - Math.abs(d - want) * 0.05;
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    /*
     * If nothing the map offers can see the player, put them in front of them.
     *
     * The posts are placed once, from the navmesh, and there is no guarantee
     * that any of them has a line to wherever the player happens to be
     * standing. When that happens the rescue relocates everyone somewhere they
     * still cannot be found, and two things circle the same wall until the
     * clock runs out. A ring of standable points around the player is the last
     * word: the floor has to be able to end.
     */
    if (engage && bestScore < 2 && this.confront(e)) return;
    if (!best) return;
    const t = toThree(best[0], best[1], best[2]);
    e.pos.set(t.x, t.y + (e.type.hover ?? 0), t.z);
    e.bestDist = Infinity;
    e.stuckFor = 0;
    e.node = -1;
  }

  /**
   * Put an enemy somewhere the player can actually see them.
   *
   * A ring of standable, visible points around the player, tried nearest
   * first. This is the floor's last resort and it ignores the map's posts
   * entirely, because the situation it exists for is precisely the one where
   * none of the posts can be seen from where the player is standing.
   */
  /**
   * Put a stalled enemy somewhere it can reach you from.
   *
   * It used to require line of sight and take the first spot that had it,
   * which meant the answer to "nothing has died for a while" was an enemy
   * appearing in the middle of your screen. That reads as the game cheating,
   * and it is the thing people notice about it.
   *
   * Two passes. The first wants a spot that has a clear line to you but is
   * *outside the frustum* — behind you, or off the edge — so what you see is
   * something walking in rather than something arriving. Only if the room
   * genuinely has no such place does the second pass take anything at all,
   * because a floor that cannot end is worse than a startling entrance.
   */
  private confront(e: Enemy): boolean {
    const cam = this.player.camera.position;
    const feet = this.player.pos;
    for (const offscreenOnly of [true, false]) {
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2 + this.rand();
        for (const d of [9.5, 12, 7, 14.5, 5.5]) {
          const at = new THREE.Vector3(feet.x + Math.cos(a) * d, feet.y, feet.z + Math.sin(a) * d);
          if (!this.standable(at)) continue;
          const eye = new THREE.Vector3(at.x, at.y + 1.2, at.z);
          if (!this.canSee(eye, cam)) continue;
          if (offscreenOnly && this.inView(eye)) continue;
          e.pos.set(at.x, at.y + (e.type.hover ?? 0), at.z);
          e.bestDist = Infinity;
          e.stuckFor = 0;
          e.node = -1;
          return true;
        }
      }
    }
    return false;
  }

  /**
   * How high the floor is under a point, for anything lying on it.
   *
   * The same collider grid the player stands on, asked the other way round:
   * the highest top at or below where the thing currently is. Bodies land on
   * a stair tread or a crate rather than through it, and nothing falls out of
   * the world.
   */
  private floorUnder(x: number, z: number, y: number): number {
    let best = -Infinity;
    for (const c of this.near(x, z)) {
      const b = c.box;
      if (b.max.x <= x - 0.05 || b.min.x >= x + 0.05) continue;
      if (b.max.z <= z - 0.05 || b.min.z >= z + 0.05) continue;
      // A generous window above: a piece dipping a little into the floor on
      // the frame it lands must still be able to see the floor it is landing
      // on, or it drops through and never finds anything again.
      if (b.max.y > y + 0.8) continue;
      if (b.max.y > best) best = b.max.y;
    }
    // Nothing underneath at all — over a gap, past an edge. Say so by
    // answering far below rather than pretending there is a floor here; the
    // caller retires anything that falls out of the world.
    return best === -Infinity ? -1e4 : best;
  }

  private updateEnemies(dt: number): void {
    const g = this.graph;
    if (!g) return;
    const cam = this.player.camera.position;
    const shape = waveShape(this.state.depth);

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      const t = e.type;
      e.hitFlash = Math.max(0, e.hitFlash - dt * 4);
      e.slow = Math.min(1, e.slow + dt * 0.6);
      e.bob += dt * 3;

      /*
       * A siphon takes from you for as long as it can see you.
       *
       * The only rule in the set whose answer is the map rather than the gun:
       * put something between you and it and it stops, immediately and
       * completely. It costs a trickle rather than a hit, so it is never the
       * thing that kills you — it is the thing that makes an open room a bad
       * place to stand and finding a wall a real decision.
       */
      if (e.elite === 'siphon' && !e.dead && e.warmup <= 0) {
        const eye = new THREE.Vector3(e.pos.x, e.pos.y + t.height * 0.6, e.pos.z);
        if (eye.distanceTo(cam) < SIPHON_RANGE && this.canSee(eye, cam)) {
          // The beam is redrawn a few times a second rather than every frame:
          // the tracer pool is small and shared with every round the player
          // fires, and a beam that eats it would silently stop their own
          // shots being drawn.
          this.siphonBeam -= dt;
          if (this.siphonBeam <= 0) {
            this.siphonBeam = 0.12;
            this.fx.tracer(eye, new THREE.Vector3(cam.x, cam.y - 0.35, cam.z), 0xff3158);
          }
          /*
           * Not through `hurt`: that is a hit, with a flash, a sound and a
           * third of a second of immunity. This is a drain.
           *
           * It still has to be counted, though. Going straight to the health
           * meant a siphon was the one thing in the game that could take a
           * player apart without appearing in the telemetry at all — floor
           * seven's damage table listed a Revenant Bulwark and a Brood Bulwark
           * and no Siphon anything, which reads as "harmless" and means
           * "invisible". Two diagnoses this session were made off that table.
           */
          const drain = Math.min(SIPHON_RATE * dt, Math.max(0, this.state.health - 1));
          this.state.health -= drain;
          this.telemetry.hurt(drain, nameOf(e));
          this.state.damageFlash = Math.max(this.state.damageFlash, 0.22);
          this.state.killedBy = nameOf(e);
        }
      }

      /*
       * Still arriving: fades up, does not move, does not shoot.
       *
       * The light is brightest at the start and gone by the end, so what you
       * see is a flare that resolves into a body — the flare is the part you
       * can catch out of the corner of an eye, and it is over by the time the
       * thing under it can do anything to you.
       */
      if (e.warmup > 0) {
        e.warmup -= dt;
        const arrived = e.warmup <= 0;
        const alpha = arrived ? 1 : 1 - e.warmup / WARMUP;
        fade(e, alpha);
        e.group.position.copy(e.pos);
        if (e.glow) {
          e.glow.intensity = arrived ? 0 : 26 * (e.warmup / WARMUP) ** 1.5;
          if (arrived) {
            this.fx.releaseSpawnLight(e.glow);
            e.glow = null;
          }
        }
        if (!arrived) continue;
        // Whatever it was going to do, it starts thinking about it now.
        e.cooldown = Math.max(e.cooldown, 0.4);
      }

      const groundY = t.hover ? e.pos.y - t.hover : e.pos.y;
      const canon = fromThree(new THREE.Vector3(e.pos.x, groundY, e.pos.z));
      const here = g.locate(canon[0], canon[1], canon[2] + 0.1, 3);
      if (here >= 0) e.node = here;

      const to = new THREE.Vector3(cam.x - e.pos.x, 0, cam.z - e.pos.z);
      const planar = to.length();
      const standoff = t.standoff ?? 0;
      const eyeline = new THREE.Vector3(e.pos.x, e.pos.y + t.height * 0.6, e.pos.z);
      const sees = this.canSee(eyeline, cam);
      // Seen, rather than aimed at. A player has met the enemy the moment it
      // is on their screen; whether it is inside the assist cone is a separate
      // question and the answer to it is several seconds later.
      if (sees && e.warmup <= 0 && this.inView(eyeline)) this.telemetry.sawEnemy();

      let desired: THREE.Vector3 | null = null;
      if (standoff && sees && planar < standoff * 1.35) {
        // Shooters hold their distance and strafe rather than closing.
        const away = planar < standoff * 0.85 ? -1 : planar > standoff * 1.15 ? 1 : 0;
        const side = new THREE.Vector3(-to.z, 0, to.x).normalize().multiplyScalar(Math.sin(e.bob * 0.4) * 0.8);
        desired = to.clone().normalize().multiplyScalar(away).add(side);
        if (desired.lengthSq() > 1e-6) desired.normalize();
      } else if (planar < 2.8 && Math.abs(cam.y - 1.6 - groundY) < 1.6) {
        desired = to.clone().normalize();
      } else if (e.node >= 0 && this.flow && this.flow.distance(e.node) > 0) {
        // Follow the gradient a step at a time and steer at the *next* node,
        // not one ten hops away. Aiming at a distant node means walking the
        // straight line to it, which cuts every corner between here and there
        // — invisible while enemies passed through walls, and the reason they
        // wedge against them now that they do not. Two hops is enough to keep
        // the motion smooth without leaving the mesh.
        let cur = e.node;
        let hops = 0;
        while (hops < 2) {
          const next = this.flow!.downhill(cur);
          if (next < 0) break;
          cur = next;
          hops++;
        }
        if (hops > 0) {
          const target = toThree(g.points[cur * 3], g.points[cur * 3 + 1], g.points[cur * 3 + 2]);
          const dx = target.x - e.pos.x;
          const dz = target.z - e.pos.z;
          const len = Math.hypot(dx, dz);
          if (len > 1e-5) desired = new THREE.Vector3(dx / len, 0, dz / len);
        }
      }

      // Last resort: head straight at the player.
      //
      // The flow field only answers for nodes the search actually reached, and
      // an enemy standing on one it did not — off the mesh after a relocate,
      // on a ledge, in a pocket the BFS never entered — otherwise has no
      // direction at all and simply stops. Walking at the player and sliding
      // along whatever it meets is worse pathing and infinitely better than
      // standing still on a floor that cannot end until it dies.
      if (!desired && !standoff && planar > 1.5) desired = to.clone().normalize();

      if (desired) {
        if (planar > t.range * 0.7) {
          for (const o of this.enemies) {
            if (o === e) continue;
            const dx = e.pos.x - o.pos.x;
            const dz = e.pos.z - o.pos.z;
            const d2 = dx * dx + dz * dz;
            const want = (t.radius + o.type.radius) * 1.15;
            if (d2 > 1e-6 && d2 < want * want) {
              const d = Math.sqrt(d2);
              desired.x += (dx / d) * 0.5;
              desired.z += (dz / d) * 0.5;
            }
          }
          desired.normalize();
        }
        const standoffMin = standoff ? standoff * 0.7 : 1.15;
        if (planar < standoffMin && !standoff) {
          const inward = (desired.x * to.x + desired.z * to.z) / (planar || 1);
          if (inward > 0) {
            desired.x -= (to.x / planar) * inward;
            desired.z -= (to.z / planar) * inward;
            if (desired.lengthSq() > 1e-6) desired.normalize();
            else desired.set(0, 0, 0);
          }
        }
        // A wretch you have hurt but not killed comes at you faster, which
        // makes finishing things the correct instinct.
        // Clamped, because "how hurt is it" is only a fraction if the health
        // is inside its own bounds. Anything that puts health above the
        // maximum — a healer, a debug tool — otherwise turns this negative and
        // the enemy is flung across the map at a hundred million metres a
        // second, which is a long way to go looking for the last one.
        const wounded = Math.max(0, Math.min(1, 1 - e.health / Math.max(1e-6, t.health * shape.healthScale)));
        const hurtRush = t.enrages ? 1 + wounded * 0.75 : 1;
        const speed = t.speed * shape.speedScale * e.slow * hurtRush;
        // Slide along walls rather than through them. Following navmesh nodes
        // keeps an enemy roughly on walkable ground, but the straight line
        // between two nodes cuts corners, and the separation and standoff
        // steering push sideways off the path entirely — which is when you see
        // one walk out of a wall.
        this.slide(e, desired.x * speed * dt, desired.z * speed * dt);
        if (desired.lengthSq() > 1e-6) e.group.rotation.y = Math.atan2(desired.x, desired.z);
      }

      /*
       * Follow the ground, whatever made it move.
       *
       * This used to live inside the branch that follows the flow field, so an
       * enemy walking a route climbed and everything else did not: the moment
       * one came within charging distance, or took up a standoff, or fell back
       * to walking straight at you, its height froze at whatever it was. A
       * kerb, a single step, the lip of a platform — anything the floor did
       * after that was simply ignored, and the enemy waded through it or hung
       * in the air above it.
       *
       * The mesh under its feet is the answer and it is already looked up
       * every frame. Doing it after the move rather than before means it reads
       * the ground it is standing on now, not the ground it has just left.
       */
      {
        /*
         * Stand on the surface under the feet, and on the step in front.
         *
         * Two attempts at this were wrong in the same way: they asked the
         * navmesh where the ground was. `locate` returns the nearest sample
         * within its search radius *in three dimensions*, so at the lip of a
         * step it happily answers with the node on the low side — and an
         * enemy that had just climbed was pulled straight back down by the
         * next frame's answer. It rose and sank and rose and sank and got
         * nowhere, which from the outside is an enemy that cannot climb.
         *
         * Geometry knows better and knows it locally. The highest collider top
         * that is under the feet, or no more than a step above them, is both
         * the floor it is standing on and the kerb it is walking into — one
         * query, no mesh, and the same rule the player's own `mount` uses.
         * Furniture is skipped: it is not on the mesh, so nothing routing by
         * the mesh may be lifted by it.
         */
        const feetY = t.hover ? e.pos.y - t.hover : e.pos.y;
        const r = t.radius * 0.9;
        let ground = -Infinity;
        for (const c of this.near(e.pos.x, e.pos.z)) {
          if (c.soft) continue;
          const b = c.box;
          if (b.max.x <= e.pos.x - r || b.min.x >= e.pos.x + r) continue;
          if (b.max.z <= e.pos.z - r || b.min.z >= e.pos.z + r) continue;
          if (b.max.y > feetY + STEP + 1e-3) continue;
          if (b.max.y > ground) ground = b.max.y;
        }
        // The mesh is still asked, but only for the node — that is what the
        // flow field is indexed by — and only as a fallback for a body that is
        // standing over nothing at all.
        const at = fromThree(new THREE.Vector3(e.pos.x, feetY, e.pos.z));
        const under = g.locate(at[0], at[1], at[2] + 0.1, 3);
        if (under >= 0) e.node = under;
        if (ground === -Infinity) ground = under >= 0 ? g.points[under * 3 + 2] : feetY;

        const want = ground + (t.hover ?? 0) + (t.hover ? Math.sin(e.bob) * 0.18 : 0);
        // A rate rather than a snap, so a step is climbed rather than
        // teleported up — and fast enough that stairs keep up with the feet.
        e.pos.y += Math.max(-9 * dt, Math.min(9 * dt, want - e.pos.y));
      }

      // Stride tracks distance covered, not time, so a slowed enemy visibly
      // slows down instead of moonwalking at the same cadence.
      const moved = desired ? t.speed * shape.speedScale * e.slow * dt : 0;
      e.stride += moved * 3.4;
      /*
       * Something coming, heard before it is seen.
       *
       * The most useful sound in the game and the easiest to overspend on:
       * twenty enemies walking is twenty voices a second. Only walkers, only
       * within earshot, and only the ones close enough that knowing about them
       * changes what you do.
       */
      if (t.hover === undefined && moved > 0) {
        const phase = Math.floor(e.stride / Math.PI);
        if (phase !== e.stepPhase) {
          e.stepPhase = phase;
          if (planar < 22) sound.step(false, [e.pos.x, e.pos.y, e.pos.z]);
        }
      }
      if (t.hover !== undefined) {
        e.limbs[0].rotation.z = e.limbs[1].rotation.z = 0;
        e.limbs[0].rotation.y = e.stride * 6 + e.bob * 4;
        e.limbs[1].rotation.y = -e.limbs[0].rotation.y;
      } else {
        const swing = Math.sin(e.stride) * 0.7;
        e.limbs[0].position.z = swing * t.radius * 0.9;
        e.limbs[1].position.z = -swing * t.radius * 0.9;
        e.limbs[0].rotation.x = -swing * 0.5;
        e.limbs[1].rotation.x = swing * 0.5;
        if (!t.frontArmour) {
          // Arms counter-swing, which is most of what makes a walk read as a walk.
          e.arms[0].position.z = -swing * t.radius * 0.5;
          e.arms[1].position.z = swing * t.radius * 0.5;
        }
      }
      /**
       * Anti-softlock.
       *
       * The floor only opens when the last enemy is dead, which turns any
       * enemy that cannot reach you into a dead run — you are left combing a
       * map for something wedged in a corner two rooms away. Rather than trust
       * the pathing to be perfect on a hundred and ten traced maps, anything
       * that stops making progress is quietly moved somewhere it can fight.
       */
      // Measured as progress toward the player, not as movement. Now that
      // enemies collide with walls instead of walking through them, one caught
      // on a corner slides back and forth along it forever — moving the whole
      // time and never getting any closer. Distance is the honest question.
      if (standoff) {
        // A shooter's whole job is line of sight, so that is what "stuck"
        // means for one. Holding a corner it cannot shoot from is the same
        // dead end as a chaser wedged on a wall, and it is the shape this took
        // in practice: a spitter parked at seventeen metres behind cover, with
        // a floor that could not end until it died.
        e.stuckFor = sees ? 0 : e.stuckFor + dt;
        if (e.stuckFor > 6) {
          this.relocate(e, true);
          e.stuckFor = 0;
        }
      } else if (planar > 4) {
        if (planar < e.bestDist - 0.5) {
          e.bestDist = planar;
          e.stuckFor = 0;
        } else {
          e.stuckFor += dt;
          if (e.stuckFor > 4) {
            this.relocate(e);
            e.stuckFor = 0;
          }
        }
      } else {
        e.stuckFor = 0;
      }

      e.group.position.set(e.pos.x, e.pos.y + (t.hover ? 0 : Math.abs(Math.sin(e.stride)) * 0.035), e.pos.z);
      (e.body.material as THREE.MeshStandardMaterial).emissiveIntensity = e.hitFlash;
      (e.head.material as THREE.MeshStandardMaterial).emissiveIntensity = e.hitFlash;

      e.cooldown -= dt;
      e.age += dt;

      /*
       * Things that expire.
       *
       * A turret is built during a fight rather than rolled into the roster,
       * and the floor cannot end while one is standing. Without a lifetime a
       * builder that outlives its own cooldown can extend a floor for ever,
       * which is a softlock wearing a hat.
       */
      if (t.decays && e.age > t.decays) {
        this.fx.burst(e.pos, 0xff3158, 14);
        e.dead = true;
        this.enemies.splice(i, 1);
        this.retire(e);
        this.settle(e);
        continue;
      }

      // What it is about to do, drawn where you can see it.
      this.drawTell(e, sees, planar);

      if (e.cooldown <= 0 && sees) {
        if (t.heals) {
          e.cooldown = t.interval;
          this.mend(e, shape);
        } else if (t.deploys) {
          e.cooldown = t.interval;
          this.deploy(e, shape);
        } else if (standoff && planar < t.range) {
          e.cooldown = t.interval;
          if (t.lobs) this.lobAtPlayer(e);
          else this.shootAtPlayer(e);
        } else if (!standoff && planar < t.range && Math.abs(cam.y - 1.6 - groundY) < 2.2) {
          e.cooldown = t.interval;
          this.hurt(t.damage * shape.damageScale, e.pos, nameOf(e));
        }
      }
    }
  }

  /**
   * Show the wind-up.
   *
   * Only for the enemies that hit hard enough to deserve it, and only while
   * they can actually see you: a line drawn from behind a wall would be
   * telling you about a shot that is not coming. The mark grows over the
   * telegraph so "nearly" and "now" look different.
   */
  private drawTell(e: Enemy, sees: boolean, planar: number): void {
    const t = e.type;
    const mat = e.tell.material as THREE.MeshBasicMaterial;
    if (!t.telegraph || !sees || e.cooldown > t.telegraph || e.cooldown <= 0 || planar > t.range) {
      if (e.tell.visible) {
        e.tell.visible = false;
        mat.opacity = 0;
      }
      return;
    }
    const wind = 1 - e.cooldown / t.telegraph;
    // Once per wind-up, at the moment it starts: a rising tone that runs for
    // exactly as long as you have to get out of the way.
    if (!e.tell.visible) sound.telegraph(e.cooldown, [e.pos.x, e.pos.y, e.pos.z], t.lobs === true);
    e.tell.visible = true;
    mat.opacity = 0.2 + wind * 0.55;
    const cam = this.player.camera.position;
    if (t.lobs) {
      // On the ground, where the shell is going. Flat, and lifted a hair so it
      // does not fight the floor for the same pixels.
      e.tell.rotation.set(-Math.PI / 2, 0, 0);
      e.tell.position.set(cam.x, this.player.pos.y + 0.05, cam.z);
      e.tell.scale.setScalar(2.6 * (0.55 + wind * 0.45));
    } else {
      // Down the lane, from the muzzle to you.
      const from = new THREE.Vector3(e.pos.x, e.pos.y + t.height * 0.72, e.pos.z);
      const mid = from.clone().lerp(cam, 0.5);
      e.tell.position.copy(mid);
      e.tell.lookAt(cam);
      const len = from.distanceTo(cam);
      e.tell.scale.set(0.02 + wind * 0.05, 0.02 + wind * 0.05, len);
      e.tell.rotation.z = 0;
    }
  }

  /**
   * A leech does nothing to you and everything to the fight.
   *
   * The tether is the point: without a visible line from the healer to what it
   * is healing, a brute that will not die reads as a bug rather than as a
   * priority.
   */
  private mend(e: Enemy, shape: { healthScale: number }): void {
    const t = e.type;
    let mended = false;
    for (const o of this.enemies) {
      if (o === e || o.dead || o.type.heals) continue;
      const full = o.type.health * shape.healthScale;
      if (o.health >= full) continue;
      if (o.pos.distanceTo(e.pos) > t.range) continue;
      o.health = Math.min(full, o.health + (t.heals ?? 0) * t.interval);
      o.hitFlash = Math.max(o.hitFlash, 0.4);
      this.fx.tracer(
        new THREE.Vector3(e.pos.x, e.pos.y + t.height * 0.6, e.pos.z),
        new THREE.Vector3(o.pos.x, o.pos.y + o.type.height * 0.6, o.pos.z),
        0xff3158,
      );
      mended = true;
    }
    if (!mended) e.cooldown = t.interval * 0.5;
  }

  /** Build a turret where it stands, up to the cap it is allowed. */
  private deploy(e: Enemy, shape: WaveShape): void {
    const t = e.type;
    const id = t.deploys;
    if (!id) return;
    const built = typeById(id);
    if (!built) return;
    const standing = this.enemies.filter((o) => o.type.id === id && !o.dead).length;
    if (standing >= (t.deployCap ?? 2)) {
      e.cooldown = t.interval * 0.4;
      return;
    }
    const made = this.acquire(built);
    made.staged = false;
    made.marked = false;
    made.health = built.health * shape.healthScale;
    made.dead = false;
    made.cooldown = 0.8;
    made.slow = 1;
    made.stuckFor = 0;
    made.bestDist = Infinity;
    made.node = e.node;
    // Beside the builder rather than inside it, and on the same floor.
    const a = this.rand() * Math.PI * 2;
    made.pos.set(e.pos.x + Math.cos(a) * 1.2, e.pos.y, e.pos.z + Math.sin(a) * 1.2);
    made.group.position.copy(made.pos);
    made.group.visible = true;
    this.enemies.push(made);
    this.fx.burst(made.pos, 0xff3158, 12);
  }

  /**
   * A shell, over whatever you are standing behind.
   *
   * Aimed at where you are now rather than where you will be: the mark on the
   * ground is a promise, and a shell that chases you would make the mark a
   * lie. Moving is supposed to work.
   */
  private lobAtPlayer(e: Enemy): void {
    sound.enemyShot(e.type, [e.pos.x, e.pos.y, e.pos.z]);
    const cam = this.player.camera.position;
    const from = new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.6, e.pos.z);
    const to = new THREE.Vector3(cam.x, this.player.pos.y + 0.2, cam.z);
    const flat = new THREE.Vector3(to.x - from.x, 0, to.z - from.z);
    const range = flat.length();
    const gravity = 26;
    // Fixed flight time, so the arc is high on a short throw and flat on a
    // long one — and so the mark and the shell agree about when it lands.
    const flight = Math.max(0.8, Math.min(2.2, 0.5 + range / 26));
    const vel = new THREE.Vector3(
      flat.x / flight,
      (to.y - from.y) / flight + 0.5 * gravity * flight,
      flat.z / flight,
    );
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 8, 6),
      new THREE.MeshBasicMaterial({ color: e.type.headColour }),
    );
    mesh.position.copy(from);
    this.root.add(mesh);
    const scale = waveShape(this.state.depth).damageScale;
    this.projectiles.push({
      mesh, vel, life: flight + 0.6, damage: e.type.damage * scale, from: from.clone(),
      gravity, splash: 3.2, by: nameOf(e),
    });
  }

  private shootAtPlayer(e: Enemy): void {
    sound.enemyShot(e.type, [e.pos.x, e.pos.y, e.pos.z]);
    const cam = this.player.camera.position;
    const from = new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.7, e.pos.z);
    const dir = cam.clone().sub(from).normalize();
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 8, 6),
      new THREE.MeshBasicMaterial({ color: e.type.headColour }),
    );
    mesh.position.copy(from);
    this.root.add(mesh);
    const scale = waveShape(this.state.depth).damageScale;
    this.projectiles.push({
      mesh, vel: dir.multiplyScalar(26), life: 3, damage: e.type.damage * scale,
      from: from.clone(), by: nameOf(e),
    });
  }

  private updateProjectiles(dt: number): void {
    const cam = this.player.camera.position;
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      // A lobbed shell falls; a bolt does not. Applied before the step so the
      // path drawn matches the path travelled.
      if (p.gravity) p.vel.y -= p.gravity * dt;
      const step = p.vel.clone().multiplyScalar(dt);
      const len = step.length();
      const hitWall = this.raycastWorld(p.mesh.position, p.vel.clone().normalize(), len);
      p.mesh.position.add(step);
      p.life -= dt;
      const near = p.mesh.position.distanceTo(cam) < 0.7;
      const spent = near || hitWall < len || p.life <= 0;
      if (near && !p.splash) this.hurt(p.damage, p.from, p.by);
      if (spent) {
        if (p.splash) {
          // Full damage at the centre, nothing at the edge. Being near a shell
          // should cost something and being caught by one should hurt.
          const d = p.mesh.position.distanceTo(cam);
          if (d < p.splash) this.hurt(p.damage * (1 - d / p.splash), p.from, p.by);
          this.fx.burst(p.mesh.position, 0xff3158, 22);
        }
        this.root.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.projectiles.splice(i, 1);
      }
    }
  }

  private updatePickups(): void {
    const cam = this.player.camera.position;
    for (let i = this.pickups.length - 1; i >= 0; i--) {
      const g = this.pickups[i];
      if (g.position.distanceTo(cam) > 1.8) continue;
      this.take(g.userData.kind as string, g.userData.variant as string | undefined);
      this.root.remove(g);
      this.pickups.splice(i, 1);
      this.discarded.push(g);
    }
  }

  /**
   * Give the player a pickup, as if they had walked over it.
   *
   * Public because it is a real operation on a run rather than an
   * implementation detail — it is how the tests hand somebody a second weapon,
   * and how anything else that wants to would.
   */
  take(kind: string, variant?: string): void {
    const s = this.state;
    s.pickupFlash = 1;
    sound.pickup(kind);
    if (kind === 'health') {
      s.health = Math.min(s.maxHealth, s.health + 35);
      this.say('+35 health', 1.4);
    } else if (kind === 'ammo') {
      // Never into the endless band: that is the sidearm's alone, and a rifle
      // that wandered into it would stop needing ammo at all.
      /*
       * A box is one magazine. Not a fraction of a reserve.
       *
       * It used to be a quarter of whatever the gun's spare allowance was,
       * which is a number nobody can feel: on a rifle that is a hundred and
       * seventy per cent of a magazine, on an LMG it is barely one, and on
       * neither does picking it up mean anything you could describe. One
       * reload is a unit — you know what you have been given the moment you
       * see the number move, and it is the same promise on every weapon.
       *
       * Every gun takes a box, the sidearm included: it is no longer a special
       * case, so there is nothing to exclude.
       */
      s.reserve = Math.min(ENDLESS - 1, s.reserve + this.magOf(s.weapon));
      s.stock[s.weapon.id] = s.reserve;
      this.say('Ammo', 1.2);
    } else if (kind === 'armor') {
      s.maxHealth += 15;
      s.health += 15;
      this.say('+15 max health', 1.6);
    } else if (kind === 'weapon') {
      const w = variant ? weaponById(variant) : dropFor(s.depth, this.rand());
      // Stow the one in your hands before it becomes the one on your back.
      s.mags[s.weapon.id] = s.ammo;
      s.stock[s.weapon.id] = s.reserve;
      if (!s.carried.includes(w.id)) s.carried.push(w.id);
      if (s.carried.length > 3) {
        const dropped = s.carried.splice(1, 1)[0];
        delete s.mags[dropped];
        delete s.stock[dropped];
      }
      s.weapon = w;
      // A gun off the floor comes loaded. Anything else and picking one up in
      // a fight is a liability rather than a rescue.
      s.ammo = this.magOf(w);
      s.mags[w.id] = s.ammo;
      // Spares come with it, and a second copy of a gun you already carry tops
      // up what you had rather than replacing it.
      s.reserve = Math.min(ENDLESS - 1, (s.stock[w.id] ?? 0) + w.pickupAmmo);
      s.stock[w.id] = s.reserve;
      s.reloading = false;
      this.sinceLoad = 0;
      this.rhythm = 0;
      /*
       * Say what it is, and the first time, say what it does.
       *
       * The rule is on the HUD permanently, so this is not where the player
       * learns it — it is where they are told a rule exists at all. Once per
       * action per run: the fourth pump shotgun does not need to explain what
       * a pump shotgun does, and a game that keeps explaining is a game that
       * thinks you were not listening.
       */
      const rule = INTRINSIC[w.action];
      this.say(`${w.name} — ${w.blurb}`, 3);
      if (!this.explained.has(w.action)) {
        this.explained.add(w.action);
        this.sayLater(`${rule.name}: ${rule.text}`, 3.1, 3.4);
      }
    }
  }

  private updateExit(): void {
    if (!this.gen) return;
    const s = this.state;
    const cam = this.player.camera.position;

    if (!s.exitOpen) {
      /*
       * The floor is done when its contract is.
       *
       * A sweep is what it always was — everything released and nothing left
       * standing — but a hunt ends the moment the marked one falls, whatever
       * else is still walking around, and a hold ends when the signal reads.
       * The stragglers on a hunt are deliberately left alive: the point of
       * the contract is that you do not have to find them.
       */
      const d = this.director;
      if (d) {
        s.remaining = d.remaining;
        if (d.done && this.toKill > 0) this.openExit();
      } else {
        s.remaining = this.enemies.length + this.roster.length;
        if (!this.roster.length && this.enemies.length === 0 && this.toKill > 0) this.openExit();
      }
      // Point at the last one standing. Being told to kill everything and not
      // being told where the last thing is turns the end of every floor into a
      // search, which is the least interesting thing this game can ask for.
      /**
       * A last guarantee that the floor can end.
       *
       * Per-enemy stuck detection catches one wedged in a corner. It does not
       * catch the other shapes this takes — one patrolling a pocket of the map
       * you have no reason to revisit, one you can see across a gap but cannot
       * reach, one on a ledge your shots do not clear. Rather than try to
       * enumerate those on a hundred and ten traced maps, the rule is simply
       * that progress must keep happening: if nothing has died for a while and
       * things are still alive, they are brought to you.
       */
      /*
       * Longer than it was, because the reason it fired has been fixed.
       *
       * Fourteen seconds without a kill is not unusual while you are crossing
       * a floor, and the rescue was firing during ordinary play rather than
       * only when something was wrong. Most of what stalled a floor was
       * enemies that could not climb a kerb — a third of every height-changing
       * edge on the mesh was blocked for them — and now that they can, this
       * can afford to wait for a real stall.
       */
      if (this.sinceKill > 22 && this.enemies.length) {
        /*
         * A hold is a siege, so the rescue is only ever "bring them here".
         *
         * Relocating on a hold floor moves the fight away from the ring the
         * player is standing in, which is the one place the floor has asked
         * them to be — the rescue would be actively fighting the contract.
         */
        const siege = this.state.contract === 'hold';
        for (const e of this.enemies) {
          if (siege) this.confront(e);
          else this.relocate(e, true);
        }
        // Second time round they stop using the map at all and are simply put
        // in front of you. Somebody circling the same wall for half a minute
        // has already had the interesting version of this fight.
        if (this.rescues++ > 0 && !siege) for (const e of this.enemies) this.confront(e);
        this.sinceKill = 0;
        this.telemetry.rescued(false);
        this.say('They are closing in', 2);
      }

      /*
       * And after half a minute of nothing dying, they are simply gone.
       *
       * On its own clock, and one that only a kill resets. The rescue above
       * resets `sinceKill` every time it fires, so counting its firings is
       * counting a thing that keeps postponing itself — which is why four
       * floors in thirty simulated runs still sat at the timeout with an enemy
       * standing sixteen metres away that nothing could reach.
       *
       * Every rescue assumes there is *somewhere* to put an enemy that the
       * player can get to: a ring of standable ground around them, a post with
       * a line of sight. On a ledge, in a stairwell, or on a map whose navmesh
       * disagrees with its geometry there may be none, and the result is a run
       * the player has already spent ten minutes on ending in a room with
       * nothing to do and no way to say so. No score for them: this is the
       * floor conceding, not the floor being won.
       */
      this.barren = this.enemies.length ? this.barren + this.lastDt : 0;
      if (this.barren > BARREN) {
        this.barren = 0;
        for (const e of [...this.enemies]) {
          this.enemies.splice(this.enemies.indexOf(e), 1);
          e.dead = true;
          this.retire(e);
          this.settle(e);
        }
        this.telemetry.rescued(true);
        this.say('They have gone to ground', 2.4);
      }

      /*
       * What the compass points at while you are still fighting.
       *
       * On a hunt it is the marked target, always and from the first second:
       * the floor has told you to kill one specific thing and then declined to
       * say where it is, which is the search this whole system exists to
       * remove. On a hold it is the signal, for the same reason. On a sweep it
       * is the last one or two standing, which is where it came from.
       */
      const at = this.objectiveMark();
      if (at) {
        s.exitDistance = Math.hypot(at.x - cam.x, at.z - cam.z);
        s.exitBearing = this.bearingTo(at);
        s.huntMode = true;
      } else {
        s.exitDistance = 0;
        s.huntMode = false;
      }
      return;
    }
    s.huntMode = false;

    const d = Math.hypot(this.exitPos.x - cam.x, this.exitPos.z - cam.z);
    this.state.exitDistance = d;
    this.state.exitBearing = this.bearingTo(this.exitPos);
    if (d < 2.1 && Math.abs(this.exitPos.y - (cam.y - 1.6)) < 3) {
      this.state.phase = 'cleared';
      this.telemetry.floorEnd(true);
      this.state.score += 100 + this.state.depth * 50;
    }
  }

  /**
   * Open the way out, right where the player is standing.
   *
   * Putting it at their feet rather than back across the map is the difference
   * between a reward and a chore: the fight ended here, so the door is here.
   */
  /**
   * Everything still lying on the floor, and what it is.
   *
   * Public for the same reason `objectivePosition` is: the practice bot can
   * only measure what it can see, and it could not see these. It walked to the
   * exit and collected whatever happened to fall within arm's reach on the
   * way, which on twenty runs meant carrying the starting pistol for
   * sixty-eight per cent of the time and the next most-used weapon for four —
   * so the "most-carried weapons" table was a description of the bot's
   * pathing and not of the library it was supposed to be exercising.
   *
   * A person walking past a gun picks the gun up. This is the information they
   * are using when they do.
   */
  loosePickups(): { at: THREE.Vector3; kind: string; variant?: string }[] {
    return this.pickups.map((p) => ({
      at: p.position.clone(),
      kind: p.userData.kind as string,
      variant: p.userData.variant as string | undefined,
    }));
  }

  /**
   * Somewhere the floor needs the player to be, or null.
   *
   * Public because the practice bot has to be able to play a contract it
   * cannot see — a hold it never walks into is a floor it reports as stuck,
   * which would make the balance numbers a measurement of the bot rather than
   * of the game. It is the same information the compass gives a person.
   */
  objectivePosition(): THREE.Vector3 | null {
    const d = this.director;
    if (!d || this.state.exitOpen) return null;
    if (d.contract === 'hold' && d.holdProgress < 1 && this.signalGroup) {
      return this.signalGroup.position.clone();
    }
    if (d.contract === 'hunt' && !d.markDown) {
      if (this.quarry && !this.quarry.dead) return this.quarry.pos.clone();
      const z = d.zones[d.markZone];
      if (z) return toThree(z.centre[0], z.centre[1], z.centre[2]);
    }
    // A sweep wants you in the live zone, which is where everything it has
    // left is coming from.
    const z = d.zones[d.active];
    return z ? toThree(z.centre[0], z.centre[1], z.centre[2]) : null;
  }

  /**
   * Where the compass should point while the floor is still running.
   *
   * Null means "nothing in particular", which on a sweep with a dozen enemies
   * left is the honest answer — pointing at one of twelve is noise.
   */
  private objectiveMark(): THREE.Vector3 | null {
    const d = this.director;
    if (d?.contract === 'hunt') {
      if (this.quarry && !this.quarry.dead) return this.quarry.pos;
      // Not out yet: point at the zone it will be in, so the walk there is
      // still a walk toward something.
      const z = d.zones[d.markZone];
      return z ? toThree(z.centre[0], z.centre[1], z.centre[2]) : null;
    }
    if (d?.contract === 'hold' && this.signalGroup) return this.signalGroup.position;
    return this.enemies.length <= 3 ? this.nearestTarget() : null;
  }

  /** Put the way out back to sleep without disturbing the shader cache. */
  private dimExit(): void {
    this.exitGroup.visible = false;
    const beacon = this.exitGroup.userData.beacon as THREE.PointLight | undefined;
    if (beacon) beacon.intensity = 0;
  }

  /** Draw the signal on the floor of the zone that holds it. */
  private placeSignal(at: [number, number, number] | null): void {
    this.clearSignal();
    if (!at || !this.gen) return;
    const g = buildSignal(accentFor(this.gen.spec.id), HOLD_RADIUS);
    const t = toThree(at[0], at[1], at[2]);
    // The zone's centre is the average of its navmesh samples, which sits at
    // walking height rather than on the floor. A ring is a floor marking.
    g.position.set(t.x, t.y - 0.02, t.z);
    this.signalGroup = g;
    this.root.add(g);
  }

  private clearSignal(): void {
    if (!this.signalGroup) return;
    this.root.remove(this.signalGroup);
    disposeTree(this.signalGroup);
    this.signalGroup = null;
  }

  private openExit(): void {
    const s = this.state;
    sound.cleared();
    s.exitOpen = true;
    s.remaining = 0;
    const feet = this.player.pos;
    // A couple of metres ahead, so it is visible rather than swallowing them.
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.player.camera.quaternion);
    const flat = new THREE.Vector3(fwd.x, 0, fwd.z);
    if (flat.lengthSq() < 1e-6) flat.set(0, 0, -1);
    flat.normalize();
    /*
     * Somewhere ahead that you can both stand on *and* walk to.
     *
     * Standable alone is not enough: three metres in front of someone pressed
     * against a wall is on the other side of it. Nor is one clear ray down the
     * middle — a ray is a line and a player is a cylinder, so a doorway the ray
     * sails through can still be one the shoulders do not, and the bot spent a
     * hundred and fifty seconds walking into the frame of one. So the walk is
     * checked the way it is actually made: the whole width of the player, at
     * knee and at chest, standing at every step along the way.
     *
     * Nearer options are tried first, and if none of them are clear it opens
     * underfoot, which is always reachable.
     */
    const side = new THREE.Vector3(-flat.z, 0, flat.x);
    const r = this.player.radius;
    const walkable = (reach: number): boolean => {
      for (let t = 0.9; t <= reach + 1e-6; t += 0.7) {
        for (const off of [-r, 0, r]) {
          const at = new THREE.Vector3(
            feet.x + flat.x * t + side.x * off,
            feet.y,
            feet.z + flat.z * t + side.z * off,
          );
          if (!this.standable(at)) return false;
          for (const h of [0.5, 1.2]) {
            const from = new THREE.Vector3(feet.x + side.x * off, feet.y + h, feet.z + side.z * off);
            if (this.raycastWorld(from, new THREE.Vector3(flat.x, 0, flat.z), t) < t) return false;
          }
        }
      }
      return true;
    };
    let chosen: THREE.Vector3 | null = null;
    for (const reach of [3.6, 2.6, 1.8]) {
      const at = new THREE.Vector3(feet.x + flat.x * reach, feet.y, feet.z + flat.z * reach);
      if (!this.standable(at) || !walkable(reach)) continue;
      chosen = at;
      break;
    }
    this.exitPos.copy(chosen ?? new THREE.Vector3(feet.x, feet.y, feet.z));
    this.exitGroup.position.copy(this.exitPos);
    this.exitGroup.visible = true;
    const beacon = this.exitGroup.userData.beacon as THREE.PointLight | undefined;
    if (beacon) beacon.intensity = 3;
    this.say('Floor clear — take the way out', 4);
  }

  /** Screen-space angle from where the player is looking to a world point. */
  private bearingTo(p: THREE.Vector3): number {
    const cam = this.player.camera;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    const flat = new THREE.Vector3(fwd.x, 0, fwd.z).normalize();
    const to = new THREE.Vector3(p.x - cam.position.x, 0, p.z - cam.position.z);
    if (to.lengthSq() < 1e-8) return 0;
    to.normalize();
    return Math.atan2(flat.x * to.z - flat.z * to.x, flat.dot(to));
  }

  /** Is there floor here and room to stand? */
  private standable(p: THREE.Vector3): boolean {
    const down = new THREE.Vector3(0, -1, 0);
    const drop = this.raycastWorld(new THREE.Vector3(p.x, p.y + 0.5, p.z), down, 1.2);
    if (drop > 1.1) return false;
    const up = this.raycastWorld(new THREE.Vector3(p.x, p.y + 0.3, p.z), new THREE.Vector3(0, 1, 0), 2.2);
    return up > 2.0;
  }

  // -------------------------------------------------------------------------

  /**
   * The enemy the player is most plausibly pointing at: visible, in range, and
   * within the assist cone, preferring the one nearest the crosshair rather
   * than the one nearest the player.
   */
  private aim(coneOverride?: number): AimTarget | null {
    const cam = this.player.camera;
    const origin = cam.position;
    const facing = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    const range = this.state.weapon.range;
    const cone = coneOverride ?? ASSIST_CONE;
    let best: AimTarget | null = null;
    for (const e of this.enemies) {
      const to = new THREE.Vector3(
        e.pos.x - origin.x,
        e.pos.y + e.type.height * 0.55 - origin.y,
        e.pos.z - origin.z,
      );
      const d = to.length();
      if (d > range || d < 1e-3) continue;
      to.divideScalar(d);
      const cos = to.dot(facing);
      if (cos <= 0) continue;
      // The body's own angular size counts too: a brute at ten metres is a
      // wide target and should not need the same precision as a runner.
      const half = Math.atan2(e.type.radius, d) + cone;
      const angle = Math.acos(Math.min(1, cos));
      if (angle > half) continue;
      if (this.raycastWorld(origin, to, d) < d - 0.25) continue;
      if (!best || angle < best.angle) best = { e, dir: to, angle, dist: d };
    }
    return best;
  }

  /** Turn the camera a little way toward whatever is in the assist cone. */
  private assist(dt: number, target: AimTarget | null): void {
    if (!target) return;
    const p = this.player;
    const wantYaw = Math.atan2(-target.dir.x, -target.dir.z);
    const wantPitch = Math.asin(Math.max(-1, Math.min(1, target.dir.y)));
    let dy = wantYaw - p.yaw;
    while (dy > Math.PI) dy -= Math.PI * 2;
    while (dy < -Math.PI) dy += Math.PI * 2;
    const dp = wantPitch - p.pitch;
    // Strongest when the target is already near the middle, so it feels like
    // the shot sticking rather than the camera being taken away from you.
    const strength = 1 - Math.min(1, target.angle / ASSIST_CONE);
    const step = ASSIST_RATE * dt * (0.35 + strength * 0.65) * this.assistStrength;
    if (step <= 0) return;
    p.yaw += Math.max(-step, Math.min(step, dy));
    p.pitch += Math.max(-step, Math.min(step, dp));
  }

  private fire(target: AimTarget | null = null): void {
    const s = this.state;
    const w = s.weapon;
    s.ammo--;
    const cam = this.player.camera;
    const origin = cam.position.clone();
    /*
     * Down the barrel, and nowhere else.
     *
     * Shots used to bend onto any enemy within about eleven degrees of the
     * crosshair — the whole fire cone, silently, on mouse as well as thumb,
     * even though the note by the input mode says magnetism is a touch-only
     * courtesy. It is not a small help: eleven degrees at twenty metres is
     * four metres of forgiveness, so most misses were quietly not misses and
     * the crosshair was decoration.
     *
     * The ray now leaves the camera along the camera's own axis and the
     * weapon's spread is the only thing that moves it. If it does not touch
     * the body it does not do damage.
     */
    void target;
    const base = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    let mult = weaponMultiplier(this.mods, w);
    /*
     * Cards that change when and how you shoot rather than how hard.
     *
     * A magazine whose first half hits like a truck and whose second half is
     * nearly useless changes when you reload; standing still to remove your
     * spread changes whether you move. Both are decisions, which fifteen per
     * cent more damage is not.
     */
    const gm = this.mods.glassMagazine;
    if (gm > 1) mult *= s.ammo >= this.magOf(w) / 2 ? gm : 0.6;

    /*
     * The rules the action itself plays by.
     *
     * These are read before `sinceShot` is cleared below, because every one of
     * them is a question about the gap since the last round — which is the
     * only thing a weapon can ask of a player that costs nothing to implement
     * and changes everything about how the weapon is used. A semi-automatic
     * that pays for patience is a different gun from one that does not, at
     * identical damage.
     */
    if (this.has('deliberate') && this.sinceShot >= DELIBERATE_GAP) mult *= DELIBERATE_BONUS;
    if (this.has('overpressure') && this.sinceShot >= OVERPRESSURE_GAP) mult *= OVERPRESSURE_BONUS;
    if (this.hasEarned('battle_rhythm')) mult *= 1 + RHYTHM_STEP * this.rhythm;
    // The last round of a burst, which is the one you are still holding the
    // trigger for and the one you have the least control over.
    const crowned = this.has('last_round') && w.action === 'burst' && this.burstLeft === 0;

    // The cone opens over a sustained burst by as much as the weapon says,
    // which is what separates something you hold down from something you tap.
    // Slamfire pays for its rate here rather than in damage: the gun is a wall
    // of shot at four metres and useless at twelve, which is exactly the
    // decision the card is selling.
    const spread = this.liveSpread();
    const pierce = w.pierce + this.mods.pierce;

    /*
     * Every third round in the magazine is something else.
     *
     * Counted from the magazine going in rather than from the ammo remaining,
     * so it is the same three rounds every time and can be aimed. A card that
     * fires a grenade on a schedule you cannot predict is a card that goes off
     * in your face.
     */
    this.sinceLoad++;
    const loaded = this.hasEarned('third_shell') && this.sinceLoad % 3 === 0;

    /** Did any pellet of this shot touch anything at all? */
    let landed = false;

    this.fx.flash(origin, base);
    this.telemetry.shot(w.name);
    sound.shot(w);

    // Kick the camera up along the pattern, and remember it so the exact same
    // amount comes back down. The shot itself has already been aimed, so the
    // round that caused the climb is never the one it spoils.
    // Scaled so a full magazine climbs about fourteen degrees: enough that
    // holding the trigger costs you something, little enough that the pattern
    // is worth learning rather than just tapping around.
    const kick = recoilKick(this.burst) * w.recoil * RECOIL_SCALE * this.mods.recoil;
    const drift = swayKick(this.burst) * w.sway * this.mods.recoil;
    this.burst++;
    this.sinceShot = 0;
    this.player.pitch = Math.min(Math.PI / 2 - 0.01, this.player.pitch + kick);
    this.player.yaw += drift;
    this.recoilOwed += kick;
    this.swayOwed += drift;
    s.recoil = this.recoilOwed;
    s.spread = this.liveSpread();

    /*
     * Spread is a disc across the barrel, not a box around the world.
     *
     * The old version added a uniform jitter to x, y and z in world space:
     * square-cornered rather than round, and a third of it spent pushing the
     * ray along its own length where it does nothing. Aiming down an axis and
     * aiming down a diagonal were different weapons. This picks a point in a
     * disc perpendicular to the shot, which is what a cone actually is.
     */
    const side = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
    const lift = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    for (let p = 0; p < w.pellets; p++) {
      const dir = base.clone();
      if (spread > 0) {
        const a = this.rand() * Math.PI * 2;
        const r = Math.sqrt(this.rand()) * spread;
        dir.addScaledVector(side, Math.cos(a) * r);
        dir.addScaledVector(lift, Math.sin(a) * r);
      }
      dir.normalize();

      const face = new THREE.Vector3();
      const wall = this.raycastWorld(origin, dir, w.range, face);
      const hits: { e: Enemy; t: number; head: boolean }[] = [];
      for (const e of this.enemies) {
        const r = this.raycastEnemy(origin, dir, e);
        if (r && r.t <= wall) hits.push({ e, t: r.t, head: r.head });
      }
      hits.sort((a, b) => a.t - b.t);
      const through = hits.slice(0, 1 + pierce);
      const end = origin.clone().addScaledVector(dir, through.length ? through[through.length - 1].t : Math.min(wall, w.range));
      // White. Your own fire belongs to the muzzle language — a hard bright
      // line and a hard bright core — and not to a per-weapon palette that
      // would put fifteen more hues in the air.
      if (p === 0) this.fx.tracer(origin.clone().addScaledVector(dir, 0.6), end, 0xf2f8ff);

      /*
       * The mark it leaves, and the dust it knocks off.
       *
       * Only when nothing was hit on the way: a round that went through a
       * hostile and into the wall behind has already said what it did, and
       * putting a second event on the wall behind the thing you shot moves the
       * eye off it. Pellets are marked too — a shotgun ought to write a
       * pattern on a wall, that is the whole read of the weapon — but only the
       * first few, so a nine-pellet load does not spend a fifth of the pool on
       * one trigger pull.
       */
      if (!through.length && wall < w.range && p < 4) {
        const at = origin.clone().addScaledVector(dir, wall);
        this.fx.hole(at, face, w.pellets > 1 ? 0.055 : 0.085 + w.damage * 0.00035);
        if (p === 0) this.fx.chips(at, origin, 2, false);
      }

      // A shot that hits nothing but wall, with somewhere to bounce to.
      if (!through.length && this.mods.ricochet > 0 && wall < w.range) {
        this.bounce(origin.clone().addScaledVector(dir, wall), w.damage * mult * this.mods.ricochet, 0xf2f8ff);
      }

      for (const h of through) {
        const head = h.head || crowned;
        let dmg = w.damage * mult * (head ? w.headshot * this.mods.headshot : 1);
        if (head && this.mods.refundOnHead > 0) {
          s.ammo = Math.min(this.magOf(w), s.ammo + this.mods.refundOnHead);
        }
        // A hit to the head works the bolt, so the next round is free of the
        // second this weapon otherwise costs.
        if (h.head && this.has('chamber')) this.chambered = true;
        landed = true;
        /*
         * Break actions at contact range.
         *
         * The one rule that pushes the player rather than pulling them: a
         * weapon that has to be within five metres to do its best work, and
         * that shoves both of you apart when it does, is a weapon that decides
         * where you stand. The recoil on yourself is not a penalty, it is the
         * reason it is fun.
         */
        if (this.has('blast') && h.t < BLAST_RANGE) {
          dmg *= BLAST_BONUS;
          const away = dir.clone().setY(0.14).normalize();
          h.e.pos.addScaledVector(away, 1.6);
          h.e.cooldown = Math.max(h.e.cooldown, 0.7);
          // Shoved back by your own weapon: velocity rather than a teleport,
          // so it fights the ground the way any other movement does.
          this.player.velocity.addScaledVector(away, -3.4);
        }
        // Concussive: a hit interrupts. Whatever it was winding up, it is not
        // doing it this second, which is what makes a pump a control weapon
        // rather than a damage one.
        if (this.has('stagger')) {
          h.e.slow = Math.min(h.e.slow, 0.45);
          h.e.cooldown = Math.max(h.e.cooldown, 0.55);
        }
        // Cold stores: it does not stop, it slows, and it keeps slowing.
        if (this.hasEarned('deep_freeze')) h.e.slow = Math.max(0.2, h.e.slow * 0.82);
        // A shield only helps against what it is facing. Getting round it is
        // the point, so the falloff is sharp rather than a flat reduction.
        const armour = h.e.type.frontArmour;
        if (armour) {
          const facing = new THREE.Vector3(Math.sin(h.e.group.rotation.y), 0, Math.cos(h.e.group.rotation.y));
          const toShooter = new THREE.Vector3(origin.x - h.e.pos.x, 0, origin.z - h.e.pos.z).normalize();
          const front = Math.max(0, facing.dot(toShooter));
          dmg *= 1 - (1 - armour) * Math.pow(front, 1.6);
        }
        sound.hit(head);
        this.damage(h.e, dmg);
        if (loaded) {
          this.blast(h.e.pos.clone().setY(h.e.pos.y + 0.5), 34, 4.4, 0.28);
        }
      }
    }

    /*
     * A rhythm is broken by a miss, not by a pause.
     *
     * Which makes it a card about accuracy rather than about uptime, and
     * therefore one that rewards the same thing the game already rewards. A
     * stack that decayed on a timer would just be a reason to never stop
     * shooting, and a run that never stops shooting has no shape at all.
     */
    // One hit per shot rather than per pellet: a shotgun that lands four of
    // nine is a hit, and counting pellets would make accuracy a property of
    // which gun you were holding.
    if (landed) this.telemetry.hit(w.name);
    if (this.hasEarned('battle_rhythm') && !landed) this.rhythm = 0;
    // The third round goes off wherever it ends up, even against a wall.
    if (loaded && !landed) {
      const spent = origin.clone().addScaledVector(base, Math.min(w.range, this.raycastWorld(origin, base, w.range)));
      this.blast(spent, 34, 4.4, 0.28);
    }
  }

  /**
   * A shot carries on from where it hit the wall.
   *
   * Not a reflection — a mirrored angle almost never has anything on the end
   * of it and the card would be a decoration. It looks for whatever the wall
   * can see and goes there, which is what makes it worth firing at a wall on
   * purpose.
   */
  private bounce(at: THREE.Vector3, damage: number, colour: number): void {
    let best: Enemy | null = null;
    let bestD = 22;
    for (const e of this.enemies) {
      const mid = new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.55, e.pos.z);
      const d = mid.distanceTo(at);
      if (d >= bestD) continue;
      const dir = mid.clone().sub(at).normalize();
      if (this.raycastWorld(at, dir, d) < d - 0.2) continue;
      best = e;
      bestD = d;
    }
    if (!best) return;
    const mid = new THREE.Vector3(best.pos.x, best.pos.y + best.type.height * 0.55, best.pos.z);
    this.fx.tracer(at, mid, colour);
    this.damage(best, damage);
  }

  /**
   * `chain` bounds sympathetic detonation. The kill has to be booked and the
   * body removed *before* on-kill effects run, or a chain that comes back
   * round to a corpse re-enters it and recurses until the stack gives out.
   */
  private damage(e: Enemy, amount: number, chain = 0): void {
    if (e.dead) return;
    e.health -= amount;
    e.hitFlash = 1;

    /*
     * A heavy hit moves a phase elite.
     *
     * Triggered on the share of its health a single hit took rather than on a
     * flat number, so it is the same rule for a Runner and for a Brute: the
     * weapons it punishes are the ones that put everything into one round,
     * and the ones that beat it are the ones that cover ground. It steps
     * sideways rather than away — backwards would just be running, and this
     * is meant to spoil your aim, not to end the fight.
     */
    if (e.elite === 'phase' && !e.dead && amount / Math.max(1, e.peak) > PHASE_TRIGGER) {
      const cam = this.player.camera.position;
      const away = new THREE.Vector3(e.pos.x - cam.x, 0, e.pos.z - cam.z);
      if (away.lengthSq() > 1e-4) {
        const side = new THREE.Vector3(-away.z, 0, away.x).normalize();
        const turn = this.rand() < 0.5 ? 1 : -1;
        const to = new THREE.Vector3(
          e.pos.x + side.x * PHASE_STEP * turn,
          e.pos.y,
          e.pos.z + side.z * PHASE_STEP * turn,
        );
        if (this.standable(new THREE.Vector3(to.x, to.y - (e.type.hover ?? 0), to.z))) {
          this.fx.burst(e.pos, 0xff3158, 10);
          e.pos.copy(to);
          e.node = -1;
          e.bestDist = Infinity;
          this.fx.burst(e.pos, 0xff3158, 10);
        }
      }
    }

    /*
     * A revenant comes back, once, at the moment you nearly have it.
     *
     * The whole of the rule is the timing: it turns at a third, so a player
     * chipping at it from cover watches the last twenty seconds of work
     * undone, and a player who commits to a burst takes it straight through.
     * It is the only elite whose answer is "be less careful".
     */
    if (e.elite === 'revenant' && !e.returned && e.health > 0 && e.health < e.peak * REVENANT_AT) {
      e.returned = true;
      e.health = e.peak * REVENANT_BACK;
      e.cooldown = Math.max(e.cooldown, 0.9);
      e.slow = Math.min(e.slow, 0.5);
      this.fx.burst(new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.5, e.pos.z), 0xff3158, 26);
      sound.spotted([e.pos.x, e.pos.y, e.pos.z]);
      this.say('It came back', 1.8);
    }
    e.slow = Math.min(e.slow, this.mods.slowOnHit);
    this.state.hitFlash = 1;
    /*
     * Chips off the shell, thrown back at whoever fired.
     *
     * Three to eight of them, and more when the round was a big one, so how
     * hard you hit something is visible on the thing you hit rather than only
     * in a number. Lit ones come off a hostile because a hostile is full of
     * light; a crate is not, and does not get them.
     */
    const share = Math.min(1, amount / Math.max(1, e.type.health));
    this.fx.chips(
      new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.55, e.pos.z),
      this.player.camera.position,
      3 + Math.round(share * 5),
      true,
    );
    /*
     * And the moment of contact, held.
     *
     * A frame or two of stillness on a substantial hit is the oldest trick
     * there is and it works because it puts the impact where the eye already
     * is. Scaled by how much of the thing's health the round took, capped
     * hard, and never applied to a graze — a freeze on every pellet of a
     * shotgun would be a stutter, not a punch.
     */
    if (share > 0.22) this.freeze = Math.min(0.04, this.freeze + 0.012 + share * 0.02);
    if (e.health > 0) return;

    e.dead = true;
    // The shell comes apart into the crystal it was made of.
    this.fx.burst(new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.5, e.pos.z), 0xff3158, 18);
    this.freeze = Math.min(0.06, this.freeze + 0.03);
    sound.kill(e.type, [e.pos.x, e.pos.y, e.pos.z]);
    const i = this.enemies.indexOf(e);
    if (i >= 0) this.enemies.splice(i, 1);
    this.retire(e);
    this.settle(e);

    const s = this.state;
    s.kills++;
    this.telemetry.kill(s.weapon.name);
    this.killedHere++;
    this.sinceKill = 0;
    this.barren = 0;
    s.score += e.type.score;
    /*
     * Collect what the last few seconds cost you.
     *
     * More for killing something that was close, which is the whole argument:
     * the answer to being hurt is to go and deal with the thing that hurt you,
     * and the game should pay for that rather than for retreating from it. A
     * kill across the map still pays, because a sniper build should not be
     * punished for being a sniper build — just less.
     */
    if (this.grey > 0) {
      const near = e.pos.distanceTo(this.player.camera.position);
      const back = Math.min(this.grey, 5 + (near < 9 ? 7 : near < 20 ? 3 : 0));
      s.health = Math.min(s.maxHealth, s.health + back);
      this.grey -= back;
      this.greyFor = Math.max(this.greyFor, 1.2);
      s.grey = this.grey;
    }
    if (this.hasEarned('battle_rhythm')) this.rhythm = Math.min(RHYTHM_CAP, this.rhythm + 1);
    if (this.mods.momentum > 0) {
      // Capped, and cleared the moment you are hit: a streak is meant to be
      // something you can lose.
      this.streak = Math.min(6, this.streak + 1);
      this.streakFor = 4;
    }
    if (this.mods.chain > 0 && chain < 3) {
      let near: Enemy | null = null;
      let nearD = 7;
      for (const o of this.enemies) {
        const d = o.pos.distanceTo(e.pos);
        if (d < nearD) { near = o; nearD = d; }
      }
      if (near) {
        this.fx.tracer(
          new THREE.Vector3(e.pos.x, e.pos.y + e.type.height * 0.5, e.pos.z),
          new THREE.Vector3(near.pos.x, near.pos.y + near.type.height * 0.5, near.pos.z),
          0xff3158,
        );
        this.damage(near, amount * this.mods.chain, chain + 1);
      }
    }
    if (this.mods.lifesteal) s.health = Math.min(s.maxHealth, s.health + this.mods.lifesteal);
    if (this.mods.scavenge > 0 && this.rand() < this.mods.scavenge) {
      const canon = fromThree(e.pos);
      this.dropPickup('health', undefined, [canon[0], canon[1], canon[2]]);
    }
    // Something worth walking over to. Tougher things drop more often, so
    // clearing the hard part of a floor is what pays for the rest of it.
    const worth = Math.min(0.3, 0.045 + e.type.score / 900);
    if (this.rand() < worth) {
      const canon = fromThree(e.pos);
      const roll = this.rand();
      if (roll < 0.4) {
        this.dropPickup('weapon', dropFor(s.depth, this.rand()).id, [canon[0], canon[1], canon[2]]);
      } else if (roll < 0.75) {
        this.dropPickup('ammo', undefined, [canon[0], canon[1], canon[2]]);
      } else {
        this.dropPickup('health', undefined, [canon[0], canon[1], canon[2]]);
      }
    }
    /*
     * A brood comes apart where it fell.
     *
     * Placed around it rather than on top of it, and given a beat of warmup
     * the same as anything else that arrives, so three things appearing at
     * contact range is survivable if you were not standing on it — which is
     * the whole rule: kill it *somewhere*, and where is up to you.
     */
    if (e.elite === 'brood' && chain < 4) {
      const spawn = typeById(BROOD_SPAWN);
      if (spawn) {
        const shape = waveShape(s.depth);
        for (let i = 0; i < BROOD_COUNT; i++) {
          const a = (i / BROOD_COUNT) * Math.PI * 2 + this.rand();
          const at = new THREE.Vector3(
            e.pos.x + Math.cos(a) * 1.7,
            e.pos.y,
            e.pos.z + Math.sin(a) * 1.7,
          );
          if (!this.standable(at)) continue;
          const made = this.acquire(spawn);
          made.type = spawn;
          made.health = Math.round(spawn.health * shape.healthScale);
          made.peak = made.health;
          made.dead = false;
          made.cooldown = 1;
          made.slow = 1;
          made.stuckFor = 0;
          made.bestDist = Infinity;
          made.node = -1;
          made.marked = false;
          made.elite = null;
          made.returned = false;
          // Counted by the floor, because these are enemies the player has to
          // deal with before a zone is clear — unlike a turret, nothing can
          // make more of them.
          made.staged = e.staged;
          made.pos.copy(at);
          made.group.scale.setScalar(1);
          made.group.position.copy(made.pos);
          made.group.visible = true;
          made.warmup = 0.45;
          fade(made, 0);
          this.enemies.push(made);
        }
        this.fx.burst(e.pos, 0xff3158, 20);
      }
    }

    // The enemy's own blast, and the player's sympathetic-detonation card,
    // are the same mechanism with different owners.
    const blast = (e.type.detonates ?? 0) + (this.mods.detonate > 0 ? this.mods.detonate : 0);
    if (blast > 0 && chain < 4) {
      for (const o of [...this.enemies]) {
        if (o.pos.distanceTo(e.pos) < 4.5) this.damage(o, blast, chain + 1);
      }
      if (e.type.detonates) {
        // A popper that dies next to you still gets you, which is the whole
        // reason to shoot it early rather than let it close.
        const d = e.pos.distanceTo(this.player.camera.position);
        if (d < 4.2) this.hurt(e.type.detonates * (1 - d / 4.2), e.pos, nameOf(e));
        this.fx.burst(e.pos, 0xff3158, 26);
      }
    }
  }

  /**
   * Warm a weapon the view model is going to be asked for.
   *
   * Set by whoever owns the view model, because the run has no business
   * knowing there is one — it only knows a gun has hit the floor and that
   * somebody may want it ready.
   */
  prefetch: ((file: string | undefined) => void) | null = null;

  /** Dev-only: stops damage so a map can be photographed from anywhere. */
  godMode = false;

  /** How many times this floor has had to drag the survivors to the player. */
  private rescues = 0;
  /**
   * Seconds with something alive and nothing dying.
   *
   * Only a kill resets it, which is the whole point: it is the one measure of
   * a stalled floor that cannot be reset by the machinery that is supposed to
   * be unstalling it.
   */
  private barren = 0;
  /** The last frame's length, for the timers that live outside `update`. */
  private lastDt = 0;
  /** What this floor's pairing asks of you, said out loud on arrival. */
  private bind = '';
  /** Kills in the current streak, and how long is left of it. */
  private streak = 0;
  private streakFor = 0;
  /** How long the player has been standing still, for the cards that reward it. */
  private stillFor = 0;

  private hurt(amount: number, from?: THREE.Vector3, by?: string): void {
    const s = this.state;
    if (s.phase !== 'playing' || this.iFrames > 0 || this.godMode) return;
    if (by) s.killedBy = by;
    this.iFrames = 0.35;
    const dealt = amount * this.mods.armour;
    this.telemetry.hurt(dealt, by ?? s.killedBy ?? 'something');
    s.health -= dealt;
    /*
     * Most of that is owed back rather than gone.
     *
     * Capped at the room above your current health, because grey health you
     * could never collect is a promise the bar is making and cannot keep.
     */
    this.grey = Math.min(s.maxHealth - Math.max(0, s.health), this.grey + dealt * GREY_SHARE);
    this.greyFor = GREY_WINDOW;
    s.damageFlash = 1;
    sound.hurt((amount * this.mods.armour) / Math.max(1, s.maxHealth));
    // Where it came from, in screen terms. Getting hit from behind with no
    // idea which way to turn is the single most confusing thing that can
    // happen to someone who has never played one of these.
    if (from) {
      const cam = this.player.camera;
      const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
      const flat = new THREE.Vector3(fwd.x, 0, fwd.z).normalize();
      const to = new THREE.Vector3(from.x - cam.position.x, 0, from.z - cam.position.z);
      if (to.lengthSq() > 1e-6) {
        to.normalize();
        s.damageBearing = Math.atan2(flat.x * to.z - flat.z * to.x, flat.dot(to));
      }
    }
    this.sinceDamage = 0;
    // Losing a streak to a single hit is the price of it.
    this.streak = 0;
    this.streakFor = 0;
    if (s.health <= 0) {
      s.health = 0;
      s.phase = 'dead';
      this.triggerHeld = false;
      this.burstLeft = 0;
    }
  }

  // -------------------------------------------------------------------------

  private raycastWorld(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    range: number,
    normal?: THREE.Vector3,
  ): number {
    let best = range;
    const ix = 1 / dir.x;
    const iy = 1 / dir.y;
    const iz = 1 / dir.z;
    for (const c of this.colliders) {
      const b = c.box;
      let t0 = (b.min.x - origin.x) * ix;
      let t1 = (b.max.x - origin.x) * ix;
      if (t0 > t1) [t0, t1] = [t1, t0];
      let u0 = (b.min.y - origin.y) * iy;
      let u1 = (b.max.y - origin.y) * iy;
      if (u0 > u1) [u0, u1] = [u1, u0];
      let v0 = (b.min.z - origin.z) * iz;
      let v1 = (b.max.z - origin.z) * iz;
      if (v0 > v1) [v0, v1] = [v1, v0];
      const near = Math.max(t0, u0, v0);
      const far = Math.min(t1, u1, v1);
      if (near > far || far < 0) continue;
      const t = near < 0 ? far : near;
      if (t < best) {
        best = t;
        /*
         * Which face it went into, for anything that wants to sit on it.
         *
         * The slab test already knows: whichever of the three near planes was
         * the last one crossed is the face. Free here, and the alternative is
         * a second pass over every collider to work out something this loop
         * had in hand and threw away.
         */
        if (normal) {
          if (near === t0) normal.set(dir.x > 0 ? -1 : 1, 0, 0);
          else if (near === u0) normal.set(0, dir.y > 0 ? -1 : 1, 0);
          else normal.set(0, 0, dir.z > 0 ? -1 : 1);
        }
      }
    }
    return best;
  }

  /*
   * A body is a cylinder, and the head is where the head is.
   *
   * This used to be an axis-aligned square column of side twice the radius,
   * so a shot down the diagonal had forty per cent further to reach than one
   * down an axis and hit anyway — the enemy was measurably wider from the
   * corners of the room. It is a circle now, which is the shape the enemy is
   * drawn as, the shape it steers as, and the shape the navmesh gives it.
   *
   * The head was the top 42cm of that column whatever was up there. On an orb
   * that is most of the creature and on a walker it is the shoulders as well
   * as the skull. It is the drawn head's own size now, off `bodies.ts`.
   */
  private raycastEnemy(origin: THREE.Vector3, dir: THREE.Vector3, e: Enemy): { t: number; head: boolean } | null {
    const r = e.type.radius;
    // Where the ray crosses the vertical band the body occupies.
    let u0 = (e.pos.y - origin.y) / dir.y;
    let u1 = (e.pos.y + e.type.height - origin.y) / dir.y;
    if (u0 > u1) [u0, u1] = [u1, u0];
    if (!Number.isFinite(u0)) { u0 = -Infinity; u1 = Infinity; }
    // And where it crosses the circle, in plan.
    const ox = origin.x - e.pos.x;
    const oz = origin.z - e.pos.z;
    const a = dir.x * dir.x + dir.z * dir.z;
    const b = 2 * (ox * dir.x + oz * dir.z);
    const c = ox * ox + oz * oz - r * r;
    if (a < 1e-9) {
      // Straight up or down: inside the circle or nothing.
      if (c > 0) return null;
    } else {
      const disc = b * b - 4 * a * c;
      if (disc < 0) return null;
      const root = Math.sqrt(disc);
      const p0 = (-b - root) / (2 * a);
      const p1 = (-b + root) / (2 * a);
      u0 = Math.max(u0, Math.min(p0, p1));
      u1 = Math.min(u1, Math.max(p0, p1));
    }
    if (u0 > u1 || u1 < 0) return null;
    const t = u0 < 0 ? u1 : u0;
    const y = origin.y + dir.y * t;
    return { t, head: y > e.pos.y + e.type.height - headDepth(e.type) };
  }

  private canSee(from: THREE.Vector3, to: THREE.Vector3): boolean {
    const dir = to.clone().sub(from);
    const len = dir.length();
    if (len < 1e-4) return true;
    dir.divideScalar(len);
    return this.raycastWorld(from, dir, len) >= len - 1e-3;
  }

  // -------------------------------------------------------------------------

  private acquire(type: EnemyType): Enemy {
    const reused = this.pool.pop();
    if (reused) {
      shapeBody(reused, type);
      this.root.add(reused.group);
      this.root.add(reused.tell);
      return reused;
    }
    const parts = makeBody(type, this.tier);
    this.root.add(parts.group, parts.tell);
    return {
      ...parts,
      stride: 0,
      pos: new THREE.Vector3(),
      health: 1, node: -1, cooldown: 0, hitFlash: 0, slow: 1, bob: 0, dead: false,
      stuckFor: 0, bestDist: Infinity, stepPhase: 0, warmup: 0, glow: null, marked: false, staged: false,
      elite: null, returned: false, peak: 1,
    };
  }

  /**
   * Tell the floor one of its own is gone.
   *
   * Separate from `retire`, which is about the body: this is about the debt.
   * A zone is clear when everything it released has stopped standing, and the
   * marked target dying is the whole of a hunt.
   */
  private settle(e: Enemy): void {
    const d = this.director;
    if (e.marked) {
      e.marked = false;
      this.quarry = null;
      e.group.scale.setScalar(1);
      if (d) d.markDown = true;
      this.say('Target down', 2.6);
    }
    e.staged = false;
  }

  private retire(e: Enemy): void {
    /*
     * Come apart before going back in the pool.
     *
     * The parts are cloned by `fell`, so the body itself is free to be reused
     * for the next arrival a frame later — which it will be. Thrown away from
     * the player, because that is where the shot came from and a body that
     * falls towards its killer looks like it was pulled.
     */
    this.fx.fell([e.body, e.head, ...e.limbs, ...e.arms], this.player.camera.position, 1);
    e.tell.visible = false;
    e.group.visible = false;
    if (e.glow) {
      this.fx.releaseSpawnLight(e.glow);
      e.glow = null;
    }
    // Whatever it was in the middle of, the next thing out of the pool is
    // solid until it says otherwise.
    e.warmup = 0;
    fade(e, 1);
    // The mark is a sibling of the body rather than a child of it — a circle
    // on the floor does not move with the thing that painted it — so it has to
    // be taken out separately or it stays there after the enemy has gone.
    this.root.remove(e.group, e.tell);
    this.pool.push(e);
  }

  private spin(dt: number): void {
    /*
     * The doorway breathes rather than spins.
     *
     * A rotating ring was a thing to look at; a doorway is a thing to walk
     * through, and rotating one would be a mistake in a world this still. The
     * opening brightens and dims on a slow cycle instead, which reads at a
     * distance and disappears once you are close enough to use it.
     */
    const sheet = this.exitGroup.userData.spin as THREE.Mesh | undefined;
    if (sheet && this.exitGroup.visible) {
      const m = sheet.material as THREE.MeshBasicMaterial;
      m.opacity = 0.22 + Math.sin(performance.now() * 0.0022) * 0.1;
    }
    /*
     * The chevron rides above the target and turns slowly.
     *
     * Turning rather than pulsing: a pulse is one more thing flashing in a
     * frame that already has muzzle flashes and hit flashes in it, and a slow
     * rotation is legible at any distance without competing with any of them.
     */
    const q = this.quarry;
    if (q && !q.dead) {
      this.quarryMark.visible = true;
      this.quarryMark.position.set(
        q.pos.x,
        q.pos.y + q.type.height * 1.28 + 0.55 + Math.sin(this.state.elapsed * 2.4) * 0.08,
        q.pos.z,
      );
      this.quarryMark.rotation.y += dt * 1.4;
    } else {
      this.quarryMark.visible = false;
    }

    // The signal brightens as it fills, which is the only readout it needs.
    const ring = this.signalGroup?.userData.ring as THREE.Mesh | undefined;
    if (ring) {
      const m = ring.material as THREE.MeshBasicMaterial;
      const p = Math.max(0, this.state.hold);
      m.opacity = 0.4 + p * 0.5 + Math.sin(this.state.elapsed * (3 + p * 7)) * 0.08;
    }

    for (const p of this.pickups) {
      const s = p.userData.spin as THREE.Mesh | undefined;
      if (s) {
        s.rotation.y += dt * 1.8;
        s.rotation.x += dt * 0.7;
        s.position.y = 0.85 + Math.sin(performance.now() * 0.003 + p.position.x) * 0.07;
      }
    }
  }
}
